// cherish3: 배틀 한글화 3차 적용됨
/**
 * Client main
 *
 * Dependencies: client-core
 *
 * Sets up the main client models: Prefs, Teams, User, and PS.
 *
 * @author Guangcong Luo <guancongluo@gmail.com>
 * @license AGPLv3
 */

import { PSConnection, PSLoginServer } from './client-connection';
import { PSModel, PSStreamModel } from './client-core';
import type { PSRoomPanel, PSRouter } from './panels';
import { ChatRoom } from './panel-chat';
import type { MainMenuRoom } from './panel-mainmenu';
import { Dex, toID, type ID } from './battle-dex';
import { BattleTextParser, type Args } from './battle-text-parser';
import type { BattleRoom } from './panel-battle';
import { Teams } from './battle-teams';
import type preact from '../js/lib/preact';

// cherish-extraction-entry:begin (patch-extraction-embed.py)
// PSRouter 가 / 로 replaceState 하기 전에 원래 iframe/직접 링크를 기억한다.
const cherishExtractionEmbed = window.name === 'cherish-extraction' || new URLSearchParams(window.location.search).has('extraction-embed');
const cherishExtractionEntryRoom = window.location.pathname.slice(1);
// cherish-extraction-entry:end
// cherish-go-entry:begin (patch-home-modes.py)
// 딥링크 `?go=school|match|tower|maze|love&src=…` — PSRouter 가 / 로 replaceState 하기 전에 읽어 둔다 (2026-10-08).
// 도착 신호 한 번 (0450, api POST /showdown/arrive): 링크 버튼 클릭은 디스코드가 알려 주지 않아 여기서 센다.
// 로그인 왕복은 쿼리를 잃으므로 sessionStorage 에 한 번 맡겨 두고, 돌아온 로드가 꺼내 쓴다(그때 nonce 가 같아 같은 행에 uid 만 채워진다).
// 주기 요청이 아니다(페이지 로드당 한 번) — §4-6 통로 규약과 무관. 실패해도 조용히.
(window as any).cherishGoQuery = window.location.search;
try {
	let cherishGoSearch = window.location.search;
	if (!/[?&]go=/.test(cherishGoSearch)) {
		// 로그인 왕복에서 돌아온 로드 — 10분 안의 보관분만 쓴다 (이미 로그인된 사람이 나중에 그냥 새로고침한 경우까지 학교로 돌리지 않게)
		const pending = sessionStorage.getItem('cherish-go-pending') || '';
		sessionStorage.removeItem('cherish-go-pending');
		const bar = pending.indexOf('|');
		if (bar > 0 && Date.now() - Number(pending.slice(0, bar)) < 10 * 60 * 1000) {
			cherishGoSearch = pending.slice(bar + 1);
			(window as any).cherishGoQuery = cherishGoSearch;
		}
	}
	const cherishGoQs = new URLSearchParams(cherishGoSearch);
	const cherishGo = cherishGoQs.get('go');
	// love = 통화방 온실 신청 카드 (2026-10-08) — 보금자리 게이트(cherish-market.tsx cwmLoveBoot)가 이 쿼리를 읽어 신청 화면을 연다
	if (cherishGo === 'school' || cherishGo === 'match' || cherishGo === 'tower' || cherishGo === 'maze' || cherishGo === 'love') {
		if (/[?&]go=/.test(window.location.search)) sessionStorage.setItem('cherish-go-pending', `${Date.now()}|${window.location.search}`);
		const cherishGoSrc = /^[a-z]{1,16}$/.test(cherishGoQs.get('src') || '') ? cherishGoQs.get('src') : 'other';
		let cherishGoNonce = sessionStorage.getItem('cherish-go-nonce') || '';
		if (!cherishGoNonce) {
			cherishGoNonce = typeof crypto !== 'undefined' && (crypto as any).randomUUID ? (crypto as any).randomUUID() : `${Date.now().toString(16)}-${Math.random().toString(16).slice(2, 14)}`;
			sessionStorage.setItem('cherish-go-nonce', cherishGoNonce);
		}
		// keepalive: 로그인 전 도착은 곧바로 디스코드로 떠나는데(member-gate·login-flow), 보통 fetch 는 페이지가 떠나면 끊긴다
		// (2026-10-08 실측: 재적용 뒤에도 nginx 에 arrive 요청이 안 닿았다). keepalive 요청은 떠난 뒤에도 끝까지 간다.
		fetch('https://cherish-world.com/api/showdown/arrive', {
			method: 'POST', credentials: 'include', keepalive: true, headers: { 'Content-Type': 'application/json' },
			body: JSON.stringify({ go: cherishGo, src: cherishGoSrc, nonce: cherishGoNonce }),
		}).catch(() => {});
	}
} catch {}
// cherish-go-entry:end
export const VERTICAL_HEADER_WIDTH = 240;
export const NARROW_MODE_HEADER_WIDTH = 280;

/**********************************************************************
 * Config
 *********************************************************************/

export interface ServerInfo {
	id: ID;
	protocol: string;
	host: string;
	port: number;
	httpport?: number;
	altport?: number;
	prefix: string;
	afd?: boolean;
	registered?: boolean;
}
export interface PSConfig {
	server: ServerInfo;
	defaultserver: ServerInfo;
	includes?: {
		mainmenuHTML?: string,
		roomlistTopHTML?: string,
		roomlistAfterFirstHTML?: string,
	} | null;
	routes: {
		root: string,
		client: string,
		dex: string,
		replays: string,
		users: string,
		teams: string,
	};
	customcolors: Record<string, string>;
	whitelist?: string[];
	testclient?: boolean;
}
export declare const Config: PSConfig;

/**********************************************************************
 * Prefs
 *********************************************************************/

/**
 * String that contains only lowercase alphanumeric characters.
 */
export type RoomID = Lowercase<string> & { __isRoomID: true };
export type TimestampOptions = 'minutes' | 'seconds' | undefined;
/**
 * * `side-by-side`: desktop layout, with battle on left and chat on right, controls below battle
 *   on phones, a toggle to switch between battle/controls and chat
 * * `top-and-bottom`: vertical phone layout, with battle on top and chat/controls on bottom
 * * `scrolling`: horizontal phone layout, fully scrollable
 */
export type BattlePanelLayout = 'side-by-side' | 'top-and-bottom' | 'scrolling';
export type BattleLayoutPreference = BattlePanelLayout | `${BattlePanelLayout}-overlay`;

const PSPrefsDefaults: { [key: string]: any } = {};

/**
 * Tracks user preferences, stored in localStorage. Contains most local
 * data, with the exception of backgrounds, teams, and session data,
 * which get their own models.
 *
 * Updates will name the key updated, so you don't need to overreact.
 */
class PSPrefs extends PSStreamModel<string | null> {
	// PREFS START HERE

	/**
	 * The theme to use. "system" matches the theme of the system accessing the client.
	 */
	theme: 'light' | 'dark' | 'system' = 'light';
	/**
	 * Disables animated GIFs, but keeps other animations enabled.
	 * Workaround for a Chrome 64 bug with GIFs.
	 * true - Disable GIFs, will be automatically re-enabled if you
	 *   switch away from Chrome 64.
	 * false - Enable GIFs all the time.
	 * null - Enable GIFs only on Chrome 64.
	 */
	nogif: boolean | null = null;

	/* Graphics Preferences */
	noanim: boolean | null = null;
	bwgfx: boolean | null = null;
	nopastgens: boolean | null = null;

	/* Chat Preferences */
	inchatpm: boolean | null = null;
	noselfhighlight: boolean | null = null;
	temporarynotifications: boolean | null = null;
	leavePopupRoom: boolean | null = null;
	refreshprompt: boolean | null = null;
	chatformatting: Record<string, boolean> = {
		hidegreentext: false,
		hideme: false,
		hidespoiler: false,
		hidelinks: false,
		hideinterstice: true,
	};
	nounlink: boolean | null = null;

	/* Battle preferences */
	ignorenicks: boolean | null = null;
	ignorespects: boolean | null = null;
	ignoreopp: boolean | null = null;
	autotimer: boolean | null = null;
	autohardcore: boolean | null = null;
	spectatefromstart: boolean | null = null;
	battlelayout: BattleLayoutPreference | null = null;
	rightpanelbattles: boolean | null = null;
	disallowspectators: boolean | null = null;
	starredformats: { [formatid: string]: true | undefined } | null = null;

	/* Teambuilder preferences */
	teameditorspacious: boolean | null = null;
	teameditorzoomoutforms: boolean | null = null;
	teameditorzoomoutsearch: boolean | null = null;

	/**
	 * Show "User joined" and "User left" messages. serverid:roomid
	 * table. Uses 1 and 0 instead of true/false for JSON packing
	 * reasons.
	 */
	showjoins: { [serverid: string]: { [roomid: string]: 1 | 0 } } | null = null;
	showdebug: boolean | null = null;
	showbattles = true;
	/**
	 * Comma-separated lists of room titles to autojoin. Single
	 * string is for Main.
	 */
	autojoin: { [serverid: string]: string } | string | null = null;
	/**
	 * List of users whose messages should be ignored. userid table.
	 * Uses 1 and 0 instead of true/false for JSON packing reasons.
	 */
	ignore: { [userid: string]: 1 | 0 } | null = null;
	/**
	 * hide = hide regular display, notify = notify on new tours, 'nonotify' | null = notify on joined tours.
	 */
	tournaments: 'hide' | 'notify' | 'nonotify' | null = null;
	/**
	 * true = one panel, false = two panels, left and right
	 */
	onepanel: boolean | 'vertical' = false;
	timestamps: { chatrooms?: TimestampOptions, pms?: TimestampOptions } = {};

	mute = false;
	effectvolume = 50;
	musicvolume = 50;
	notifvolume = 50;
	uploadprivacy = true;

	afd: boolean | 'sprites' = undefined!;

	highlights: Record<string, string[]> | null = null;
	logtimes: { [serverid: ID]: { [roomid: RoomID]: number } } | null = null;

	avatar: string | null = null;
	serversettings: {
		blockPMs?: boolean | string,
		blockChallenges?: boolean,
		language?: string,
	} = {};

	// PREFS END HERE

	storageEngine: 'localStorage' | 'iframeLocalStorage' | '' = '';
	storage: { [k: string]: any } = {};
	readonly origin = `https://${Config.routes.client}`;
	constructor() {
		super();

		for (const key in this) {
			const value = (this as any)[key];
			if (['storage', 'subscriptions', 'origin', 'storageEngine', 'updates'].includes(key)) continue;
			if (typeof value === 'function') continue;
			PSPrefsDefaults[key] = value;
		}

		// set up local loading
		try {
			if (window.localStorage) {
				this.storageEngine = 'localStorage';
				this.load(JSON.parse(localStorage.getItem('showdown_prefs')!) || {}, true);
			}
		} catch {}
	}
	/**
	 * Change a preference.
	 */
	set<T extends keyof PSPrefs>(key: T, value: PSPrefs[T] | null) {
		if (value === null) {
			delete this.storage[key];
			(this as any)[key] = PSPrefsDefaults[key];
		} else {
			this.storage[key] = value;
			(this as any)[key] = value;
		}
		this.update(key);
		this.save();
	}

	load(newPrefs: object, noSave?: boolean) {
		this.fixPrefs(newPrefs);
		Object.assign(this, PSPrefsDefaults);
		this.storage = newPrefs;
		for (const key in PSPrefsDefaults) {
			if (key in newPrefs) (this as any)[key] = (newPrefs as any)[key];
		}

		this.setAFD();
		this.setShowDebug();

		this.update(null);
		if (!noSave) this.save();
	}
	save() {
		switch (this.storageEngine) {
		case 'localStorage':
			localStorage.setItem('showdown_prefs', JSON.stringify(this.storage));
		}
	}
	fixPrefs(newPrefs: any) {
		const oldShowjoins = newPrefs['showjoins'];
		if (oldShowjoins !== undefined && typeof oldShowjoins !== 'object') {
			const showjoins: { [serverid: string]: { [roomid: string]: 1 | 0 } } = {};
			const serverShowjoins: { [roomid: string]: 1 | 0 } = { global: (oldShowjoins ? 1 : 0) };
			const showroomjoins = newPrefs['showroomjoins'] as { [roomid: string]: boolean };
			for (const roomid in showroomjoins) {
				serverShowjoins[roomid] = (showroomjoins[roomid] ? 1 : 0);
			}
			delete newPrefs['showroomjoins'];
			showjoins[Config.server.id] = serverShowjoins;
			newPrefs['showjoins'] = showjoins;
		}

		// incorrect storage of serversettings
		delete newPrefs['blockPMs'];
		delete newPrefs['blockChallenges'];
		delete newPrefs['language'];

		const isChrome64 = navigator.userAgent.includes(' Chrome/64.');
		if (newPrefs['nogif'] !== undefined) {
			if (!isChrome64) {
				delete newPrefs['nogif'];
			}
		} else if (isChrome64) {
			newPrefs['nogif'] = true;
			PS.alert('Your version of Chrome has a bug that makes animated GIFs freeze games sometimes, so certain animations have been disabled. Only some people have the problem, so you can experiment and enable them in the Options menu setting "Disable GIFs for Chrome 64 bug".');
		}

		const colorSchemeQuerySupported = window.matchMedia?.('(prefers-color-scheme: dark)').media !== 'not all';
		if (newPrefs['theme'] === 'system' && !colorSchemeQuerySupported) {
			newPrefs['theme'] = 'light';
		}
		if (newPrefs['dark'] !== undefined) {
			if (newPrefs['dark']) {
				newPrefs['theme'] = 'dark';
			}
			delete newPrefs['dark'];
		}
	}

	setAFD(mode?: typeof this['afd']) {
		if (mode === undefined) {
			if (Config.server?.afd) {
				mode = true;
			} else if (this.afd !== undefined) {
				mode = this.afd;
			} else {
				// uncomment on April Fools' Day
				// mode = true;
			}
		}

		Dex.afdMode = mode;
		if (mode === true) Dex.loadTextData('en-afd');
	}
	setShowDebug(mode = this.showdebug) {
		const css = mode ? `.debug {display: block;}` : `.debug {display: none;}`;
		let style = document.querySelector('style[id=debugstyle]');
		if (style) {
			style.innerHTML = css;
		} else {
			style = document.createElement('style');
			style.id = "debugstyle";
			style.innerHTML = css;
			document.querySelector('head')?.append(style);
		}
	}
	doAutojoin() {
		let autojoin = PS.prefs.autojoin;
		if (autojoin) {
			if (typeof autojoin === 'string') {
				autojoin = { showdown: autojoin };
			}
			let rooms = autojoin[PS.server.id] || '';
			for (let title of rooms.split(",")) {
				const id = /[^a-z0-9-]/.test(title) ? toID(title) as any as RoomID : title as RoomID;
				PS.addRoom({ id, title, connected: 'pending', autofocus: false });
			}
			const cmd = `/autojoin ${rooms}`;
			// Don't jam up the queue with duplicate autojoin requests.
			// Sending multiple autojoins is a server error.
			if (PS.connection?.queue.includes(cmd)) return;
			PS.send(cmd);
		}

		for (const roomid in PS.rooms) {
			const room = PS.rooms[roomid]!;
			if (room.type === 'battle' && room.connectMode === 'pending-reconnect') {
				// The server autojoins ongoing battles.
				// Fortunately, joining a joined room is a no-op.
				room.connect();
			}
		}
	}
}

/**********************************************************************
 * Teams
 *********************************************************************/

export interface Team {
	name: string;
	format: ID;
	folder: string;
	/** Note that this can be wrong if `.uploaded?.notLoaded` */
	packedTeam: string;
	/** The icon cache must be cleared (to `null`) whenever `packedTeam` is modified */
	iconCache: preact.ComponentChildren;
	/** Used in roomids (`team-[key]`) to refer to the team. Always persists within
	  * a single session, but not always between refreshes. As long as a team still
		* exists, pointers to a Team are equivalent to a key. */
	key: string;
	isBox: boolean;
	/** uploaded team ID. will not exist for teams that are not uploaded. tracked locally */
	teamid?: number;
	/** `uploaded` will only exist if you're logged into the correct account. otherwise teamid is still tracked */
	uploaded?: {
		teamid: number,
		/** Promise = loading. */
		notLoaded: boolean | Promise<void>,
		/** password, if private. null = public, undefined = unknown, not loaded yet */
		private?: string | null,
	};
	/** team at the point it was last uploaded. outside of `uploaded` so it can track loading state */
	uploadedPackedTeam?: string;
}
interface UploadedTeam {
	name: string;
	teamid: number;
	format: ID;
	/** comma-separated list of species, for generating the icon cache */
	team: string;
	/** password, if private */
	private?: string | null;
}
if (!window.BattleFormats) window.BattleFormats = {};

/**
 * This model tracks teams and formats, updating when either is updated.
 */
class PSTeams extends PSStreamModel<'team' | 'format'> {
	/** false if it uses the ladder in the website */
	usesLocalLadder = false;
	list: Team[] = [];
	byKey: { [key: string]: Team | undefined } = {};
	deletedTeams: [Team, number][] = [];
	uploading: Team | null = null;
	constructor() {
		super();
		try {
			this.unpackAll(localStorage.getItem('showdown_teams'));
		} catch {}
	}
	teambuilderFormat(format: string): ID {
		const ruleSepIndex = format.indexOf('@@@');
		if (ruleSepIndex >= 0) format = format.slice(0, ruleSepIndex);
		const formatid = toID(format);
		if (!window.BattleFormats) return formatid;
		const formatEntry = BattleFormats[formatid];
		return formatEntry?.teambuilderFormat || formatid;
	}
	getKey(name: string) {
		const baseKey: string = toID(name) || '0';
		let key = baseKey;
		let i = 1;
		while (key in this.byKey) {
			i++;
			key = `${baseKey}-${i}`;
		}
		return key;
	}
	unpackAll(buffer: string | null) {
		if (!buffer) {
			this.list = [];
			return;
		}

		if (buffer.startsWith('[') && !buffer.trim().includes('\n')) {
			this.unpackOldBuffer(buffer);
			return;
		}

		this.list = [];
		for (const line of buffer.split('\n')) {
			const team = this.unpackLine(line);
			if (team) this.push(team);
		}
		this.update('team');
	}
	push(team: Team) {
		team.key = this.getKey(team.name);
		this.list.push(team);
		this.byKey[team.key] = team;
	}
	unshift(team: Team) {
		team.key = this.getKey(team.name);
		this.list.unshift(team);
		this.byKey[team.key] = team;
	}
	delete(team: Team) {
		const teamIndex = this.list.indexOf(team);
		if (teamIndex < 0) return false;
		this.deletedTeams.push([team, teamIndex]);
		this.list.splice(teamIndex, 1);
		delete this.byKey[team.key];
	}
	undelete() {
		if (!this.deletedTeams.length) return;
		const [team, teamIndex] = this.deletedTeams.pop()!;
		this.list.splice(teamIndex, 0, team);
		if (this.byKey[team.key]) team.key = this.getKey(team.name);
		this.byKey[team.key] = team;
	}
	spliceIn(index: number, teams: Team[]) {
		for (const team of teams) {
			team.key ||= this.getKey(team.name);
			this.byKey[team.key] = team;
		}
		this.list.splice(index, 0, ...teams);
	}
	unpackOldBuffer(buffer: string) {
		PS.alert(`Your team storage format is too old for PS. You'll need to upgrade it at https://${Config.routes.client}/recoverteams.html`);
		this.list = [];
	}
	packAll(teams: Team[]) {
		return teams.map(team => (
			(team.teamid ? `${team.teamid}[` : '') +
			(team.format || team.isBox ? `${team.format || ''}${team.isBox ? '-box' : ''}]` : ``) +
			(team.folder ? `${team.folder}/` : ``) +
			team.name + `|` + team.packedTeam
		)).join('\n');
	}
	save() {
		try {
			localStorage.setItem('showdown_teams', this.packAll(this.list));
		} catch {}
		this.update('team');
	}
	unpackLine(line: string): Team | null {
		const pipeIndex = line.indexOf('|');
		if (pipeIndex < 0) return null;
		let bracketIndex = line.indexOf(']');
		if (bracketIndex > pipeIndex) bracketIndex = -1;
		let leftBracketIndex = line.indexOf('[');
		if (leftBracketIndex < 0) leftBracketIndex = 0;
		const isBox = line.slice(0, bracketIndex).endsWith('-box');
		let slashIndex = line.lastIndexOf('/', pipeIndex);
		if (slashIndex < 0) slashIndex = bracketIndex; // line.slice(slashIndex + 1, pipeIndex) will be ''
		let format = bracketIndex > 0 ? line.slice(
			(leftBracketIndex ? leftBracketIndex + 1 : 0), isBox ? bracketIndex - 4 : bracketIndex
		) : 'gen9';
		if (!format.startsWith('gen')) format = 'gen6' + format;
		const name = line.slice(slashIndex + 1, pipeIndex);
		const teamid = leftBracketIndex > 0 ? Number(line.slice(0, leftBracketIndex)) : undefined;
		return {
			name,
			format: format as ID,
			folder: line.slice(bracketIndex + 1, slashIndex > 0 ? slashIndex : bracketIndex + 1),
			packedTeam: line.slice(pipeIndex + 1),
			iconCache: null,
			key: '',
			isBox,
			teamid,
		};
	}
	loadRemoteTeams() {
		PSLoginServer.query('getteams').then(data => {
			if (!data) return;
			if (data.actionerror) {
				return PS.alert('Error loading uploaded teams: ' + data.actionerror);
			}
			const teams: { [key: string]: UploadedTeam } = {};
			for (const team of data.teams) {
				teams[team.teamid] = team;
			}

			const NOT_LOADED_REGEX = /^[^|]*\|\|\|\|\|\|\|\|\|\|\|(?:\][^|]*\|\|\|\|\|\|\|\|\|\|\|)*$/;
			// find exact teamid matches
			for (const localTeam of this.list) {
				if (localTeam.teamid) {
					const team = teams[localTeam.teamid];
					if (!team) {
						continue;
					}
					localTeam.uploaded = {
						teamid: team.teamid,
						notLoaded: NOT_LOADED_REGEX.test(localTeam.packedTeam),
						private: team.private,
					};
					delete teams[localTeam.teamid];
				}
			}

			// do best-guess matches for teams that don't have a local team with matching teamid
			for (const team of Object.values(teams)) {
				let matched = false;
				for (const localTeam of this.list) {
					if (localTeam.teamid) continue;

					const compare = this.compareTeams(team, localTeam);
					if (compare === 'rename') {
						if (!localTeam.name.endsWith(' (local version)')) localTeam.name += ' (local version)';
					} else if (compare) {
						// prioritize locally saved teams over remote
						// as so to not overwrite changes
						matched = true;
						localTeam.teamid = team.teamid;
						localTeam.uploaded = {
							teamid: team.teamid,
							notLoaded: NOT_LOADED_REGEX.test(localTeam.packedTeam),
							private: team.private,
						};
						break;
					}
				}
				if (!matched) {
					const newTeam: Team = {
						name: team.name,
						format: team.format,
						folder: '',
						packedTeam: this.unloadedPackedTeam(team.team),
						iconCache: null,
						isBox: false,
						key: this.getKey(team.name),
						teamid: team.teamid,
						uploaded: {
							teamid: team.teamid,
							notLoaded: true,
							private: team.private,
						},
					};
					this.push(newTeam);
				}
			}
		});
	}
	unloadedPackedTeam(uploadedTeam: string) {
		const mons = uploadedTeam.split(',').map((m: string) => ({ species: m, moves: [] }));
		return Teams.pack(mons);
	}
	loadTeam(team: Team | undefined | null, ifNeeded: true): void | Promise<void>;
	loadTeam(team: Team | undefined | null): Promise<void>;
	loadTeam(team: Team | undefined | null, ifNeeded?: boolean): void | Promise<void> {
		if (!team?.uploaded || team.uploadedPackedTeam) return ifNeeded ? undefined : Promise.resolve();
		if (team.uploaded.notLoaded && team.uploaded.notLoaded !== true) return team.uploaded.notLoaded;

		const notLoaded = team.uploaded.notLoaded;
		return (team.uploaded.notLoaded = PSLoginServer.query('getteam', {
			teamid: team.uploaded.teamid,
		}).then(data => {
			if (!team.uploaded) return;
			if (!data?.team) {
				PS.alert(`Failed to load team: ${data?.actionerror || "Error unknown. Try again later."}`);
				return;
			}
			team.uploaded.notLoaded = false;
			team.uploadedPackedTeam = data.team;
			if (notLoaded) {
				team.packedTeam = data.team;
				PS.teams.save();
			}
		}));
	}
	compareTeams(serverTeam: UploadedTeam, localTeam: Team) {
		// TODO: decide if we want this
		// if (serverTeam.teamid === localTeam.teamid && localTeam.teamid) return true;

		// if titles match exactly and mons are the same, assume they're the same team
		// if they don't match, it might be edited, but we'll go ahead and add it to the user's
		// teambuilder since they may want that old version around. just go ahead and edit the name
		let sanitize = (name: string) => (name || "").replace(/\s+\(server version\)/g, '').trim();
		const nameMatches = sanitize(serverTeam.name) === sanitize(localTeam.name);
		if (!(nameMatches && serverTeam.format === localTeam.format)) {
			return false;
		}
		// if it's been edited since, invalidate the team id on this one (count it as new)
		// and load from server
		const mons = serverTeam.team.split(',').map(toID).sort().join(',');
		const otherMons = Teams.unpackSpeciesOnly(localTeam.packedTeam).map(toID).sort().join(',');
		if (mons !== otherMons) return 'rename';
		return true;
	}
}

/**********************************************************************
 * User
 *********************************************************************/

export type PSLoginState = { error?: string, success?: true, name?: string, needsPassword?: true, needsGoogle?: true };
class PSUser extends PSStreamModel<PSLoginState | null> {
	name = "";
	group = '';
	userid = "" as ID;
	named = false;
	away = false;
	registered: { name: string, userid: ID } | null = null;
	avatar = "lucas";
	challstr = '';
	loggingIn: string | null = null;
	initializing = true;
	gapiLoaded = false;
	nameRegExp: RegExp | null = null;
	setName(fullName: string, named: boolean, avatar: string) {
		const loggingIn = (!this.named && named);
		const { name, group } = BattleTextParser.parseNameParts(fullName);
		this.name = name;
		this.group = group;
		this.userid = toID(name);
		this.named = named;
		this.avatar = avatar;
		this.away = fullName.endsWith('@!');
		this.update(null);
		if (loggingIn) {
			for (const roomid in PS.rooms) {
				const room = PS.rooms[roomid]!;
				if (room.connectMode === 'pending-login') room.connect();
			}
		}
		this.updateRegExp();
	}
	validateName(name: string): string {
		// | , ; are not valid characters in names
		name = name.replace(/[|,;]+/g, '');
		const replaceList = {
			'A': 'ＡⱯȺ', 'B': 'ＢƂƁɃ', 'C': 'ＣꜾȻ', 'D': 'ＤĐƋƊƉꝹ', 'E': 'ＥƐƎ', 'F': 'ＦƑꝻ', 'G': 'ＧꞠꝽꝾ', 'H': 'ＨĦⱧⱵꞍ', 'I': 'ＩƗ', 'J': 'ＪɈ', 'K': 'ＫꞢ', 'L': 'ＬꝆꞀ', 'M': 'ＭⱮƜ', 'N': 'ＮȠƝꞐꞤ', 'O': 'ＯǪǬØǾƆƟꝊꝌ', 'P': 'ＰƤⱣꝐꝒꝔ', 'Q': 'ＱꝖꝘɊ', 'R': 'ＲɌⱤꝚꞦꞂ', 'S': 'ＳẞꞨꞄ', 'T': 'ＴŦƬƮȾꞆ', 'U': 'ＵɄ', 'V': 'ＶƲꝞɅ', 'W': 'ＷⱲ', 'X': 'Ｘ', 'Y': 'ＹɎỾ', 'Z': 'ＺƵȤⱿⱫꝢ', 'a': 'ａąⱥɐ', 'b': 'ｂƀƃɓ', 'c': 'ｃȼꜿↄ', 'd': 'ｄđƌɖɗꝺ', 'e': 'ｅɇɛǝ', 'f': 'ｆḟƒꝼ', 'g': 'ｇɠꞡᵹꝿ', 'h': 'ｈħⱨⱶɥ', 'i': 'ｉɨı', 'j': 'ｊɉ', 'k': 'ｋƙⱪꝁꝃꝅꞣ', 'l': 'ｌſłƚɫⱡꝉꞁꝇ', 'm': 'ｍɱɯ', 'n': 'ｎƞɲŉꞑꞥ', 'o': 'ｏǫǭøǿɔꝋꝍɵ', 'p': 'ｐƥᵽꝑꝓꝕ', 'q': 'ｑɋꝗꝙ', 'r': 'ｒɍɽꝛꞧꞃ', 's': 'ｓꞩꞅẛ', 't': 'ｔŧƭʈⱦꞇ', 'u': 'ｕưừứữửựųṷṵʉ', 'v': 'ｖʋꝟʌ', 'w': 'ｗⱳ', 'x': 'ｘ', 'y': 'ｙɏỿ', 'z': 'ｚƶȥɀⱬꝣ', 'AA': 'Ꜳ', 'AE': 'ÆǼǢ', 'AO': 'Ꜵ', 'AU': 'Ꜷ', 'AV': 'ꜸꜺ', 'AY': 'Ꜽ', 'DZ': 'ǱǄ', 'Dz': 'ǲǅ', 'LJ': 'Ǉ', 'Lj': 'ǈ', 'NJ': 'Ǌ', 'Nj': 'ǋ', 'OI': 'Ƣ', 'OO': 'Ꝏ', 'OU': 'Ȣ', 'TZ': 'Ꜩ', 'VY': 'Ꝡ', 'aa': 'ꜳ', 'ae': 'æǽǣ', 'ao': 'ꜵ', 'au': 'ꜷ', 'av': 'ꜹꜻ', 'ay': 'ꜽ', 'dz': 'ǳǆ', 'hv': 'ƕ', 'lj': 'ǉ', 'nj': 'ǌ', 'oi': 'ƣ', 'ou': 'ȣ', 'oo': 'ꝏ', 'ss': 'ß', 'tz': 'ꜩ', 'vy': 'ꝡ',
		};
		const normalizeList = {
			'A': 'ÀÁÂẦẤẪẨÃĀĂẰẮẴẲȦǠÄǞẢÅǺǍȀȂẠẬẶḀĄ', 'B': 'ḂḄḆ', 'C': 'ĆĈĊČÇḈƇ', 'D': 'ḊĎḌḐḒḎ', 'E': 'ÈÉÊỀẾỄỂẼĒḔḖĔĖËẺĚȄȆẸỆȨḜĘḘḚ', 'F': 'Ḟ', 'G': 'ǴĜḠĞĠǦĢǤƓ', 'H': 'ĤḢḦȞḤḨḪ', 'I': 'ÌÍÎĨĪĬİÏḮỈǏȈȊỊĮḬ', 'J': 'Ĵ', 'K': 'ḰǨḲĶḴƘⱩꝀꝂꝄ', 'L': 'ĿĹĽḶḸĻḼḺŁȽⱢⱠꝈ', 'M': 'ḾṀṂ', 'N': 'ǸŃÑṄŇṆŅṊṈ', 'O': 'ÒÓÔỒỐỖỔÕṌȬṎŌṐṒŎȮȰÖȪỎŐǑȌȎƠỜỚỠỞỢỌỘ', 'P': 'ṔṖ', 'Q': '', 'R': 'ŔṘŘȐȒṚṜŖṞ', 'S': 'ŚṤŜṠŠṦṢṨȘŞⱾ', 'T': 'ṪŤṬȚŢṰṮ', 'U': 'ÙÚÛŨṸŪṺŬÜǛǗǕǙỦŮŰǓȔȖƯỪỨỮỬỰỤṲŲṶṴ', 'V': 'ṼṾ', 'W': 'ẀẂŴẆẄẈ', 'X': 'ẊẌ', 'Y': 'ỲÝŶỸȲẎŸỶỴƳ', 'Z': 'ŹẐŻŽẒẔ', 'a': 'ẚàáâầấẫẩãāăằắẵẳȧǡäǟảåǻǎȁȃạậặḁ', 'b': 'ḃḅḇ', 'c': 'ćĉċčçḉƈ', 'd': 'ḋďḍḑḓḏ', 'e': 'èéêềếễểẽēḕḗĕėëẻěȅȇẹệȩḝęḙḛ', 'f': '', 'g': 'ǵĝḡğġǧģǥ', 'h': 'ĥḣḧȟḥḩḫẖ', 'i': 'ìíîĩīĭïḯỉǐȉȋịįḭ', 'j': 'ĵǰ', 'k': 'ḱǩḳķḵ', 'l': 'ŀĺľḷḹļḽḻ', 'm': 'ḿṁṃ', 'n': 'ǹńñṅňṇņṋṉ', 'o': 'òóôồốỗổõṍȭṏōṑṓŏȯȱöȫỏőǒȍȏơờớỡởợọộ', 'p': 'ṕṗ', 'q': '', 'r': 'ŕṙřȑȓṛṝŗṟ', 's': 'śṥŝṡšṧṣṩșşȿ', 't': 'ṫẗťṭțţṱṯ', 'u': 'ùúûũṹūṻŭüǜǘǖǚủůűǔȕȗụṳ', 'v': 'ṽṿ', 'w': 'ẁẃŵẇẅẘẉ', 'x': 'ẋẍ', 'y': 'ỳýŷỹȳẏÿỷẙỵƴ', 'z': 'źẑżžẓẕ',
		};
		const replaceRegexes: [RegExp, string][] = [];
		for (const i in replaceList) {
			replaceRegexes.push([new RegExp('[' + replaceList[i as 'A'] + ']', 'g'), i]);
		}
		const normalizeRegexes: [RegExp, string][] = [];
		for (const i in normalizeList) {
			normalizeRegexes.push([new RegExp('[' + normalizeList[i as 'A'] + ']', 'g'), i]);
		}

		for (const [regex, replacement] of replaceRegexes) {
			name = name.replace(regex, replacement);
		}
		for (const [regex, replacement] of normalizeRegexes) {
			name = name.replace(regex, replacement);
		}
		return name.trim();
	}
	changeName(name: string) {
		name = this.validateName(name);
		const userid = toID(name);
		if (!userid) {
			this.updateLogin({ name, error: "Usernames must contain at least one letter." });
			return;
		}

		if (userid === this.userid) {
			PS.send(`/trn ${name}`);
			this.update({ success: true });
			return;
		}
		this.loggingIn = name;
		this.update(null);
		PSLoginServer.rawQuery(
			'getassertion', { userid, challstr: this.challstr }
		).then(res => {
			this.handleAssertion(name, res);
			this.updateRegExp();
		});
	}
	changeNameWithPassword(name: string, password: string, special: PSLoginState = { needsPassword: true }) {
		this.loggingIn = name;
		if (!password && !special) {
			this.updateLogin({
				name,
				error: "Password can't be empty.",
				...special as any,
			});
		}
		this.update(null);
		PSLoginServer.query(
			'login', { name, pass: password, challstr: this.challstr }
		).then(data => {
			this.loggingIn = null;
			if (data?.curuser?.loggedin) {
				// success!
				const username = data.curuser.loggedin.username;
				this.registered = { name: username, userid: toID(username) };
				this.handleAssertion(name, data.assertion);
			} else {
				// wrong password
				if (special.needsGoogle) {
					try {
						// @ts-expect-error gapi included dynamically
						gapi.auth2.getAuthInstance().signOut();
					} catch {}
				}
				this.updateLogin({
					name,
					error: data?.error || 'Wrong password.',
					...special as any,
				});
			}
		});
	}
	updateLogin(update: PSLoginState) {
		this.update(update);
		if (!PS.rooms['login']) {
			PS.join('login' as RoomID, { args: update });
		}
	}
	handleAssertion(name: string, assertion?: string | null) {
		if (!assertion) {
			// cherish-login-flow: 영어 원문 대신 우리 문구
			PS.alert('로그인에 실패했어요. 잠시 뒤 다시 시도해 주세요.');
			return;
		}
		this.loggingIn = null;
		if (assertion.slice(0, 14).toLowerCase() === '<!doctype html') {
			// some sort of MitM proxy; ignore it
			const endIndex = assertion.indexOf('>');
			if (endIndex > 0) assertion = assertion.slice(endIndex + 1);
		}
		if (assertion.startsWith('\r')) assertion = assertion.slice(1);
		if (assertion.startsWith('\n')) assertion = assertion.slice(1);
		if (assertion.includes('<')) {
			// cherish-login-flow: 영어 원문 대신 우리 문구
			PS.alert('로그인 서버와 연결이 방해받고 있어요. 다른 네트워크(데이터·와이파이)로 바꾸거나 잠시 뒤 다시 시도해 주세요.');
			return;
		}
		if (assertion === ';') {
			this.updateLogin({ name, needsPassword: true });
		} else if (assertion === ';;@gmail') {
			this.updateLogin({ name, needsGoogle: true });
		} else if (assertion.startsWith(';;')) {
			this.updateLogin({ error: assertion.slice(2) });
		} else if (assertion.includes('\n') || !assertion) {
			// cherish-login-flow: 영어 원문 대신 우리 문구
			PS.alert('로그인 서버와 연결이 방해받고 있어요. 잠시 뒤 다시 시도해 주세요.');
		} else {
			PS.send(`/trn ${name},0,${assertion}`);
			this.update({ success: true });
		}
	}
	logOut() {
		PSLoginServer.query(
			'logout', { userid: this.userid }
		);
		PS.send(`/logout`);
		PS.connection?.disconnect();

		// cherish-login-flow: 영어 원문 대신 우리 문구 (개명 안내는 우리 규칙에 없다)
		PS.alert('로그아웃했어요. 다시 들어오려면 새로고침해 주세요.');
		this.name = "";
		this.group = '';
		this.userid = "" as ID;
		this.named = false;
		this.registered = null;
		this.update(null);
	}

	updateRegExp() {
		if (!this.named) {
			this.nameRegExp = null;
		} else {
			let escaped = this.name.replace(/[^A-Za-z0-9]+$/, '');
			// we'll use `,` as a sentinel character to mean "any non-alphanumeric char"
			// unicode characters can be replaced with any non-alphanumeric char
			for (let i = escaped.length - 1; i > 0; i--) {
				if (/[^ -~]/.test(escaped[i])) {
					escaped = escaped.slice(0, i) + ',' + escaped.slice(i + 1);
				}
			}
			escaped = escaped.replace(/[[\]/{}()*+?.\\^$|-]/g, "\\$&");
			escaped = escaped.replace(/,/g, "[^A-Za-z0-9]?");
			this.nameRegExp = new RegExp('(?:\\b|(?!\\w))' + escaped + '(?:\\b|\\B(?!\\w))', 'i');
		}
	}
}

/**********************************************************************
 * Server
 *********************************************************************/

interface PSGroup {
	name?: string;
	type?: 'leadership' | 'staff' | 'punishment' | 'normal' | 'user';
	order: number;
}

class PSServer {
	id = Config.defaultserver.id;
	host = Config.defaultserver.host;
	port = Config.defaultserver.port;
	httpport = Config.defaultserver.httpport;
	altport = Config.defaultserver.altport;
	registered = Config.defaultserver.registered;
	prefix = '/showdown';
	protocol: 'http' | 'https' = Config.defaultserver.httpport ? 'https' : 'http';
	groups: { [symbol: string]: PSGroup } = {
		'#': {
			name: "Room Owner (#)",
			type: 'leadership',
			order: 101,
		},
		'~': {
			name: "Administrator (~)",
			type: 'leadership',
			order: 102,
		},
		'&': {
			name: "Administrator (&)",
			type: 'leadership',
			order: 103,
		},
		'\u2605': {
			name: "Host (\u2605)",
			type: 'staff',
			order: 104,
		},
		'@': {
			name: "Moderator (@)",
			type: 'staff',
			order: 105,
		},
		'%': {
			name: "Driver (%)",
			type: 'staff',
			order: 106,
		},
		// by default, unrecognized ranks go here, between driver and bot
		'*': {
			name: "Bot (*)",
			order: 109,
		},
		'\u2606': {
			name: "Player (\u2606)",
			order: 110,
		},
		'+': {
			name: "Voice (+)",
			order: 200,
		},
		' ': {
			order: 201,
		},
		'!': {
			name: "Muted (!)",
			type: 'punishment',
			order: 301,
		},
		'✖': {
			name: "Namelocked (\u2716)",
			type: 'punishment',
			order: 302,
		},
		'\u203d': {
			name: "Locked (\u203d)",
			type: 'punishment',
			order: 303,
		},
	};
	defaultGroup: PSGroup = {
		order: 108,
	};
	defaultGroupSymbol = ' ';
	getGroup(symbol: string | undefined) {
		return this.groups[(symbol || this.defaultGroupSymbol).charAt(0)] || this.defaultGroup;
	}
	parseGroups(groupsList: string) {
		let data: { symbol?: string, name?: string, type?: PSGroup['type'] }[];
		try {
			data = JSON.parse(groupsList);
		} catch {
			return;
		}
		if (!data) return;

		const groups: { [symbol: string]: PSGroup } = {};
		this.defaultGroup = { order: 10006.5 };
		this.defaultGroupSymbol = ' ';
		for (let i = 0; i < data.length; i++) {
			const entry = data[i];
			if (!entry) continue;

			const symbol = entry.symbol || ' ';
			const groupName = entry.name;
			const groupType = entry.type || 'user';

			if (groupType === 'normal') this.defaultGroup = { order: i + 0.5 };
			if (!groupName) this.defaultGroupSymbol = symbol;

			groups[symbol] = {
				name: groupName ? `${groupName} (${symbol})` : undefined,
				type: groupType,
				order: i + 1,
			};
		}
		this.groups = groups;
	}
}

/**********************************************************************
 * Rooms
 *********************************************************************/

type PSRoomLocation = 'left' | 'right' | 'popup' | 'mini-window' | 'modal-popup';

export interface PSRoomFocusOptions {
	preventScroll?: boolean;
}

export interface RoomOptions {
	id: RoomID;
	title?: string;
	/** @see {PS.roomTypes} */
	type?: string;
	location?: PSRoomLocation | null;
	/**
	 * In case the room received messages before it was ready for them.
	 */
	backlog?: Args[] | null;
	/**
	 * Popup parent element. If it exists, a popup shows up right above/below that element.
	 *
	 * No effect on non-popup panels.
	 */
	parentElem?: HTMLElement | null;
	/**
	 * Popup's parent room. Inferred from `parentElem`. Closes any popup that isn't this popup.
	 *
	 * No effect on non-popup panels.
	 */
	parentRoomid?: RoomID | null;
	/** Opens the popup to the right of its parent, instead of the default above/below (for userlists) */
	rightPopup?: boolean;
	connected?: PSRoom['connected'];
	connectMode?: PSRoom['connectMode'];
	/** @see {PSRoomPanelSubclass#noURL} */
	noURL?: boolean;
	args?: Record<string, unknown> | null;
}

interface PSNotificationState {
	title: string;
	body?: string;
	/** Used to identify notifications to be dismissed - '' if you only want to autodismiss */
	id: string;
	/** normally: automatically dismiss the notification when viewing the room; set this to require manual dismissing */
	noAutoDismiss: boolean;
	notification?: Notification | null;
}

type ClientCommands<RoomT extends PSRoom> = {
	/** return true to send the original command on to the server, or a string to send that command */
	[command: Lowercase<string>]: (
		this: RoomT, target: string, cmd: string, element: HTMLElement | null
	) => string | boolean | null | void,
};
/** The command signature is a lie but TypeScript and string validation amirite? */
type ParsedClientCommands = {
	[command: `parsed${string}`]: (
		this: PSRoom, target: string, cmd: string, element: HTMLElement | null
	) => string | boolean | null | void,
};

export function makeLoadTracker() {
	let resolver: () => void;
	const tracker: Promise<void> & { loaded: () => void } = new Promise<void>(resolve => {
		resolver = resolve;
	}) as any;
	tracker.loaded = () => {
		resolver();
	};
	return tracker;
}

/**
 * As a PSStreamModel, PSRoom can emit `Args` to mean "we received a message",
 * and `null` to mean "tell Preact to re-render this room"
 */
export class PSRoom extends PSStreamModel<Args | null> implements RoomOptions {
	id: RoomID;
	title = "";
	type = '';
	isPlaceholder = false;
	/** unused??? */
	readonly classType: string = '';
	location: PSRoomLocation = 'left';
	/**
	 * Whether a modal popup is closable by clicking on the
	 * background or pressing Esc. It is, of course, still closable
	 * through other methods.
	 */
	closable = true;
	/**
	 * Whether the room is connected to the server.
	 *
	 * * `'pending'` after sending `/join`.
	 *
	 * * `'init'` after getting a response from the server, while handling
	 *   the lines after receiving `'init'` from the server.
	 */
	connected: boolean | 'pending' | 'init' = false;
	/**
	 * How this room connects to the server.
	 * * `null` = client-only
	 * * `'normal'` = can connect normally
	 * * `'pending-reconnect'` = once connected, now not (flag to retry after reconnect)
	 * * `'pending-login'` = failed to connect (flag to retry after login)
	 * * `'deleted'` = deleted server-side, but should remain visible client-side
	 * * `'not-found'` = got `noinit` from the server
	 */
	connectMode: null | 'normal' | 'deleted' | 'not-found' | 'pending-reconnect' | 'pending-login' = null;
	onRequestFocus: ((options?: PSRoomFocusOptions) => boolean | void) | null = null;
	onParentKeyDown: ((e?: Event) => boolean | void) | null = null;

	width = 0;
	height = 0;
	/**
	 * Preact means that the DOM state lags behind the app state. This means
	 * rooms frequently have `display: none` at the time we want to focus them.
	 * And popups sometimes initialize hidden, to calculate their position from
	 * their width/height without flickering. But hidden HTML elements can't be
	 * focused, so this is a note-to-self to focus the next time they can be.
	 */
	focusNextUpdate: boolean | PSRoomFocusOptions = false;
	parentElem: HTMLElement | null = null;
	parentRoomid: RoomID | null = null;
	rightPopup = false;

	notifications: PSNotificationState[] = [];
	isSubtleNotifying = false;

	/** only affects mini-windows */
	minimized = false;
	caughtError: string | undefined;
	/** @see {PSRoomPanelSubclass#noURL} */
	noURL: boolean;
	args: Record<string, unknown> | null;

	constructor(options: RoomOptions) {
		super();
		this.id = options.id;
		this.title = options.title || this.title || this.id;
		if (options.type) this.type = options.type;
		if (options.location) this.location = options.location;
		if (options.parentElem) this.parentElem = options.parentElem;
		if (options.parentRoomid) this.parentRoomid = options.parentRoomid;
		if (this.location !== 'popup' && this.location !== 'modal-popup') this.parentElem = null;
		if (options.rightPopup) this.rightPopup = true;
		if (options.connected) this.connected = options.connected;
		if (options.connectMode !== undefined) this.connectMode = options.connectMode;
		if (options.backlog) this.backlog = options.backlog;
		this.noURL = options.noURL || false;
		this.args = options.args || null;
	}
	getParent() {
		if (this.parentRoomid) return PS.rooms[this.parentRoomid] || null;
		return null;
	}
	notify(options: { title: string, body?: string, noAutoDismiss?: boolean, id?: string }) {
		let desktopNotification: Notification | null = null;
		const roomIsFocused = document.hasFocus?.() && PS.isVisiblePanel(this);
		if (roomIsFocused && !options.noAutoDismiss) return;
		if (!roomIsFocused) {
			PS.playNotificationSound();
			try {
				desktopNotification = new Notification(options.title, { body: options.body });
				if (desktopNotification) {
					desktopNotification.onclick = () => {
						window.focus();
						PS.focusRoom(this.id);
					};
					if (PS.prefs.temporarynotifications) {
						setTimeout(() => { desktopNotification?.close(); }, 5000);
					}
				}
			} catch {}
		}
		if (options.noAutoDismiss && !options.id) {
			throw new Error(`Must specify id for manual dismissing`);
		}
		if (options.id) {
			this.notifications = this.notifications.filter(notification => notification.id !== options.id);
		}
		this.notifications.push({
			title: options.title,
			body: options.body,
			id: options.id || '',
			noAutoDismiss: options.noAutoDismiss || false,
			notification: desktopNotification,
		});
		PS.update();
	}
	subtleNotify() {
		if (PS.isVisiblePanel(this)) return;
		const room = PS.rooms[this.id] as ChatRoom;
		const lastSeenTimestamp = PS.prefs.logtimes?.[PS.server.id]?.[this.id] || 0;
		const lastMessageTime = +(room.lastMessage?.[1] || 0);
		this.isSubtleNotifying = !((lastMessageTime + room.timeOffset) <= lastSeenTimestamp);
		PS.update();
	}
	dismissNotificationAt(i: number) {
		try {
			this.notifications[i].notification?.close();
		} catch {}
		this.notifications.splice(i, 1);
	}
	dismissNotification(id: string) {
		const index = this.notifications.findIndex(n => n.id === id);
		if (index !== -1) {
			this.dismissNotificationAt(index);
		}
		PS.update();
	}
	autoDismissNotifications() {
		let room = PS.rooms[this.id] as ChatRoom;
		if (room.lastViewedTime) {
			// Mark chat messages as read to avoid double-notifying on reload
			let lastMessageDates = PS.prefs.logtimes || {};
			if (!lastMessageDates[PS.server.id]) lastMessageDates[PS.server.id] = {};
			lastMessageDates[PS.server.id][room.id] = room.lastViewedTime || 0;
			PS.prefs.set('logtimes', lastMessageDates);
		}
		for (let i = this.notifications.length - 1; i >= 0; i--) {
			if (!this.notifications[i].noAutoDismiss) {
				this.dismissNotificationAt(i);
			}
		}
		this.isSubtleNotifying = false;
	}
	interruptClose(explicit?: boolean, elem?: HTMLElement | null): string | boolean {
		return false;
	}
	connect(): void {
		throw new Error(`This room is not designed to connect to a server room`);
	}
	/**
	 * By default, a reconnected room will receive the init message as a bunch
	 * of `receiveLine`s as normal. Before that happens, handleReconnect is
	 * called, and you can return true to stop that behavior. You could also
	 * prep for a bunch of `receiveLine`s and then not return anything.
	 */
	handleReconnect(msg: string): boolean | void {}
	receiveLine(args: Args): void {
		switch (args[0]) {
		case 'title': {
			this.title = args[1];
			PS.update();
			break;
		} case 'notify': {
			const [, title, body, toHighlight] = args;
			if (toHighlight && !ChatRoom.getHighlight(toHighlight, this.id)) break;
			this.notify({ title, body });
			break;
		} case 'tempnotify': {
			const [, id, title, body, toHighlight] = args;
			if (toHighlight && !ChatRoom.getHighlight(toHighlight, this.id)) break;
			this.notify({ title, body, id });
			break;
		} case 'tempnotifyoff': {
			const [, id] = args;
			this.dismissNotification(id);
			break;
		} default: {
			this.update(args);
		}
		}
	}
	/**
	 * Used only by commands; messages from the server go directly from
	 * `PS.receive` to `room.receiveLine`
	 */
	add(line: string, ifChat?: boolean) {
		if (this.type !== 'chat' && this.type !== 'battle') {
			if (!ifChat) {
				PS.mainmenu.handlePM(PS.user.userid, PS.user.userid);
				PS.rooms['dm-' as RoomID]?.receiveLine(BattleTextParser.parseLine(line));
			}
		} else {
			this.receiveLine(BattleTextParser.parseLine(line));
		}
	}
	errorReply(message: string, element = this.currentElement) {
		if (element?.tagName === 'BUTTON' || element?.tagName === 'SELECT') {
			PS.alert(message, { parentElem: element });
		} else {
			this.add(`|error|${message}`);
		}
	}
	parseClientCommands(commands: ClientCommands<this>) {
		const parsedCommands: ParsedClientCommands = {};
		for (const cmd in commands) {
			const names = cmd.split(',').map(name => name.trim());
			for (const name of names) {
				if (name.includes(' ')) throw new Error(`Client command names cannot contain spaces: ${name}`);
				// good luck convincing TypeScript that these types are compatible
				parsedCommands[name as 'parsed'] = commands[cmd as 'cmd'] as any;
			}
		}
		return parsedCommands;
	}
	globalClientCommands = this.parseClientCommands({
		'j,join'(target, cmd, elem) {
			target = PS.router.extractRoomID(target) || target;
			const roomid = /[^a-z0-9-]/.test(target) ? toID(target) as any as RoomID : target as RoomID;
			PS.join(roomid, { parentElem: elem });
		},
		'part,leave,close'(target, cmd, elem) {
			const roomid = (/[^a-z0-9-]/.test(target) ? toID(target) as any as RoomID : target as RoomID) || this.id;
			const room = PS.rooms[roomid];

			if (room?.interruptClose(!!target, elem)) return;

			PS.leave(roomid);
		},
		'closeand'(target) {
			// we actually do the close last, because a lot of things stop working
			// after you delete the room
			this.send(target);
			PS.leave(this.id);
		},
		'receivepopup'(target) {
			PS.alert(target);
		},
		'inopener,inparent'(target) {
			// do this command in the popup opener
			let room = this.getParent();
			if (room && PS.isPopup(room)) room = room.getParent();
			// will crash if the parent doesn't exist, which is fine
			room!.send(target);
		},
		'maximize'(target) {
			const roomid = /[^a-z0-9-]/.test(target) ? toID(target) as any as RoomID : target as RoomID;
			const targetRoom = roomid ? PS.rooms[roomid] : this;
			if (!targetRoom) return this.errorReply(`Room '${roomid}' not found.`);
			if (PS.isPanel(targetRoom)) {
				this.errorReply(`'${roomid}' is already maximized.`);
			} else if (!PS.isPopup(targetRoom)) {
				PS.moveRoom(targetRoom, 'left', false, 0);
				PS.update();
			} else {
				this.errorReply(`'${roomid}' is a popup and can't be maximized.`);
			}
		},
		'logout'() {
			PS.user.logOut();
		},
		'reconnect,connect'() {
			if (this.connected) {
				if (this.connected !== true) return this.errorReply(`You are already connecting.`);
				return this.errorReply(`You are already connected.`);
			}

			if (!PS.isOffline) {
				// connect to room
				try {
					this.connect();
				} catch (err: any) {
					this.errorReply(err.message);
				}
				return;
			}

			// connect to server
			const uptime = Date.now() - PS.startTime;
			if (uptime > 24 * 60 * 60 * 1000) {
				// cherish-day-refresh-cmd:begin (patch-alert-mask.py)
				// 유저가 직접 누른 경로라 매번 묻는다 (자동 경로의 한 번 제한은 cherish-day-refresh).
				PS.confirm(`창을 연 지 하루가 넘었어요.\n새로고침하면 다시 이어져요.`, {
					okButton: '새로고침', cancelButton: '나중에',
				}).then(confirmed => {
					if (confirmed) this.send(`/refresh`);
				});
				// cherish-day-refresh-cmd:end
				return;
			}
			PSConnection.connect();
		},
		'refresh'() {
			document.location.reload();
		},
		'workoffline'() {
			if (PS.isOffline) {
				return this.errorReply(`You are already offline.`);
			}
			PS.connection?.disconnect();
		},
		'cancelsearch'() {
			if (PS.mainmenu.cancelSearch()) {
				this.add(`||Search cancelled.`, true);
			} else {
				this.errorReply(`You're not currently searching.`);
			}
		},
		'disallowspectators'(target) {
			PS.prefs.set('disallowspectators', target !== 'off');
		},
		'star'(target) {
			const id = toID(target);
			if (!window.BattleFormats[id] && !/^gen[1-9]$/.test(id)) {
				this.errorReply(`Format ${id} does not exist`);
				return;
			}
			let starred = PS.prefs.starredformats || {};
			starred[id] = true;
			PS.prefs.set('starredformats', starred);
			this.add(`||Added format ${id} to favourites`);
			this.update(null);
		},
		'unstar'(target) {
			const id = toID(target);
			if (!window.BattleFormats[id] && !/^gen[1-9]$/.test(id)) {
				this.errorReply(`Format ${id} does not exist`);
				return;
			}
			let starred = PS.prefs.starredformats || {};
			if (!starred[id]) {
				this.errorReply(`${id} is not in your favourites!`);
				return;
			}
			delete starred[id];
			PS.prefs.set('starredformats', starred);
			this.add(`||Removed format ${id} from favourites`);
			this.update(null);
		},
		'nick'(target, cmd, element) {
			const noNameChange = PS.user.userid === toID(target);
			if (!noNameChange) PS.join('login' as RoomID, { parentElem: element });
			if (target) {
				PS.user.changeName(target);
			}
		},
		// cherish-noavatar: 아바타는 체리쉬홈에서 정한다. 여기서 바꾸면 서버가 준
		// #cherish- 아바타가 덮여 본인 아바타만 사라지는 자해가 된다.
		'avatar'() {
			PS.alert('아바타는 체리쉬 홈에서 바꿀 수 있어요.');
		},
		'open,user'(target, cmd, elem) {
			const roomid = (toID(target) ? `user-${toID(target)}` : `users`) as RoomID;
			PS.join(roomid, {
				args: { username: target }, parentElem: elem,
			});
		},
		'ignore'(target) {
			const ignore = PS.prefs.ignore || {};
			if (!target) return true;
			if (toID(target) === PS.user.userid) {
				this.add(`||You are not able to ignore yourself.`);
			} else if (ignore[toID(target)]) {
				this.add(`||User '${target}' is already on your ignore list. ` +
					`(Moderator messages will not be ignored.)`);
			} else {
				ignore[toID(target)] = 1;
				this.add(`||User '${target}' ignored. (Moderator messages will not be ignored.)`);
				PS.prefs.set("ignore", ignore);
			}
		},
		'unignore'(target) {
			const ignore = PS.prefs.ignore || {};
			if (!target) return false;
			if (!ignore[toID(target)]) {
				this.add(`||User '${target}' isn't on your ignore list.`);
			} else {
				ignore[toID(target)] = 0;
				this.add(`||User '${target}' no longer ignored.`);
				PS.prefs.set("ignore", ignore);
			}
		},
		'clearignore'(target) {
			if (toID(target) !== 'confirm') {
				this.add("||Are you sure you want to clear your ignore list?");
				this.add('|html|If you\'re sure, use <code>/clearignore confirm</code>');
				return false;
			}
			let ignoreList = PS.prefs.ignore || {};
			if (!Object.keys(ignoreList).length) return this.add("You have no ignored users.");
			PS.prefs.set('ignore', null);
			this.add("||Your ignore list was cleared.");
		},
		'ignorelist'(target) {
			let ignoreList = Object.keys(PS.prefs.ignore || {});
			if (ignoreList.length === 0) {
				this.add('||You are currently not ignoring anyone.');
			} else {
				let ignoring: string[] = [];
				for (const key in PS.prefs.ignore) {
					if (PS.prefs.ignore[key] === 1) ignoring.push(key);
				}
				if (!ignoring.length) return this.add('||You are currently not ignoring anyone.');
				this.add(`||You are currently ignoring: ${ignoring.join(', ')}`);
			}
		},
		'showjoins'(target) {
			let showjoins = PS.prefs.showjoins || {};
			let serverShowjoins = showjoins[PS.server.id] || {};
			if (target) {
				let room = toID(target);
				if (serverShowjoins['global']) {
					delete serverShowjoins[room];
				} else {
					serverShowjoins[room] = 1;
				}
				this.add(`||Join/leave messages in room ${room}: ALWAYS ON`);
			} else {
				serverShowjoins = { global: 1 };
				this.add(`||Join/leave messages: ALWAYS ON`);
			}
			showjoins[PS.server.id] = serverShowjoins;
			PS.prefs.set("showjoins", showjoins);
		},
		'hidejoins'(target) {
			let showjoins = PS.prefs.showjoins || {};
			let serverShowjoins = showjoins[PS.server.id] || {};
			if (target) {
				let room = toID(target);
				if (!serverShowjoins['global']) {
					delete serverShowjoins[room];
				} else {
					serverShowjoins[room] = 0;
				}
				this.add(`||Join/leave messages on room ${room}: OFF`);
			} else {
				serverShowjoins = { global: 0 };
				this.add(`||Join/leave messages: OFF`);
			}
			showjoins[PS.server.id] = serverShowjoins;
			PS.prefs.set('showjoins', showjoins);
		},
		'showdebug'() {
			PS.prefs.set('showdebug', true);
			this.add('||Debug battle messages: ON');
			PS.prefs.setShowDebug(true);
		},
		'hidedebug'() {
			PS.prefs.set('showdebug', false);
			this.add('||Debug battle messages: OFF');
			PS.prefs.setShowDebug(false);
		},
		'showbattles'() {
			PS.prefs.set('showbattles', true);
			this.add('||Battle Messages: ON');
		},
		'hidebattles'() {
			PS.prefs.set('showbattles', false);
			this.add('||Battle Messages: HIDDEN');
		},
		'afd'(target) {
			if (!target) return this.send('/help afd');
			let mode = toID(target);
			if (mode === 'sprites') {
				PS.prefs.set('afd', 'sprites');
				PS.prefs.setAFD('sprites');
				this.add('||April Fools\' Day mode set to SPRITES.');
			} else if (mode === 'off') {
				PS.prefs.set('afd', null);
				PS.prefs.setAFD();
				this.add('||April Fools\' Day mode set to OFF temporarily.');
				this.add('||Trying to turn it off permanently? Use /afd never');
			} else if (mode === 'default') {
				PS.prefs.setAFD();
				PS.prefs.set('afd', null);
				this.add('||April Fools\' Day mode set to DEFAULT (Currently ' + (Dex.afdMode ? 'FULL' : 'OFF') + ').');
			} else if (mode === 'full') {
				PS.prefs.set('afd', true);
				PS.prefs.setAFD(true);
				this.add('||April Fools\' Day mode set to FULL.');
			} else if (target === 'never') {
				PS.prefs.set('afd', false);
				PS.prefs.setAFD(false);
				this.add('||April Fools\' Day mode set to NEVER.');
				if (Config.server?.afd) {
					this.add('||You\'re using the AFD URL, which will still override this setting and enable AFD mode on refresh.');
				}
			} else {
				if (target) this.add('||AFD option "' + target + '" not recognized');
				let curMode = PS.prefs.afd as string | boolean;
				if (curMode === true) curMode = 'FULL';
				if (curMode === false) curMode = 'NEVER';
				if (curMode) curMode = curMode.toUpperCase();
				if (!curMode) curMode = 'DEFAULT (currently ' + (Dex.afdMode ? 'FULL' : 'OFF') + ')';
				this.add('||AFD is currently set to ' + curMode);
				this.send('/help afd');
			}
			void Dex.loadTextData().then(() => {
				for (const roomid in PS.rooms) {
					const battle = (PS.rooms[roomid] as BattleRoom)?.battle;
					if (battle) battle.resetToCurrentTurn();
				}
			});
		},
		'clearpms'() {
			let rooms = PS.miniRoomList.filter(roomid => roomid.startsWith('dm-'));
			if (!rooms.length) return this.add('||You do not have any PM windows open.');
			for (const roomid of rooms) {
				PS.leave(roomid);
			}
			this.add("||All PM windows cleared and closed.");
		},
		'unpackhidden'() {
			if (PS.prefs.nounlink) {
				this.add('||(Already visible.)');
			}
			PS.prefs.set('nounlink', true);
			this.add('||Locked/banned users\' chat messages: VISIBLE');
		},
		'packhidden'() {
			if (!PS.prefs.nounlink) {
				this.add('||(Already hidden.)');
			}
			PS.prefs.set('nounlink', false);
			this.add('||Locked/banned users\' chat messages: HIDDEN');
		},
		'hl,highlight'(target) {
			let highlights = PS.prefs.highlights || {};
			if (target.includes(' ')) {
				let targets = target.split(' ');
				let subCmd = targets[0];
				targets = targets.slice(1).join(' ').match(/([^,]+?({\d*,\d*})?)+/g) as string[];
				// trim the targets to be safe
				for (let i = 0, len = targets.length; i < len; i++) {
					targets[i] = targets[i].replace(/\n/g, '').trim();
				}
				switch (subCmd) {
				case 'add': case 'roomadd': {
					let key = subCmd === 'roomadd' ? (PS.server.id + '#' + this.id) : 'global';
					let highlightList = highlights[key] || [];
					for (let i = 0, len = targets.length; i < len; i++) {
						if (!targets[i]) continue;
						if (/[\\^$*+?()|{}[\]]/.test(targets[i])) {
							// Catch any errors thrown by newly added regular expressions so they don't break the entire highlight list
							try {
								new RegExp(targets[i]);
							} catch (e: any) {
								return this.errorReply(
									e.message.startsWith('Invalid regular expression: ') ?
										e.message :
										'Invalid regular expression: /' + targets[i] + '/: ' + e.message
								);
							}
						}
						if (highlightList.includes(targets[i])) {
							return this.errorReply(`${targets[i]} is already on your highlights list.`);
						}
					}
					highlights[key] = highlightList.concat(targets);
					this.add(`||Now highlighting on ${(key === 'global' ? "(everywhere): " : "(in " + key + "): ")} ${highlights[key].join(', ')}`);
					// We update the regex
					ChatRoom.updateHighlightRegExp(highlights);
					break;
				}
				case 'delete': case 'roomdelete': {
					let key = subCmd === 'roomdelete' ? (PS.server.id + '#' + this.id) : 'global';
					let highlightList = highlights[key] || [];
					let newHls: string[] = [];
					for (let i = 0, len = highlightList.length; i < len; i++) {
						if (!targets.includes(highlightList[i])) {
							newHls.push(highlightList[i]);
						}
					}
					highlights[key] = newHls;
					this.add(`||Now highlighting on ${(key === 'global' ? "(everywhere): " : "(in " + key + "): ")} ${highlights[key].join(', ')}`);
					// We update the regex
					ChatRoom.updateHighlightRegExp(highlights);
					break;
				}
				default:
					// Wrong command
					this.errorReply('Invalid /highlight command.');
					this.handleSend('/help highlight'); // show help
					return;
				}
				PS.prefs.set('highlights', highlights);
			} else {
				if (['clear', 'roomclear', 'clearall'].includes(target)) {
					let key = (target === 'roomclear' ? (PS.server.id + '#' + this.id) : (target === 'clearall' ? '' : 'global'));
					if (key) {
						highlights[key] = [];
						this.add(`||All highlights (${(key === 'global' ? "everywhere" : "in " + key)}) cleared.`);
						ChatRoom.updateHighlightRegExp(highlights);
					} else {
						PS.prefs.set('highlights', null);
						this.add("||All highlights (in all rooms and globally) cleared.");
						ChatRoom.updateHighlightRegExp({});
					}
				} else if (['show', 'list', 'roomshow', 'roomlist'].includes(target)) {
					// Shows a list of the current highlighting words
					let key = target.startsWith('room') ? (PS.server.id + '#' + this.id) : 'global';
					if (highlights[key] && highlights[key].length > 0) {
						this.add(`||Current highlight list ${(key === 'global' ? "(everywhere): " : "(in " + key + "): ")}${highlights[key].join(", ")}`);
					} else {
						this.add(`||Your highlight list${(key === 'global' ? '' : ' in ' + key)} is empty.`);
					}
				} else {
					// Wrong command
					this.errorReply('Invalid /highlight command.');
					this.handleSend('/help highlight'); // show help
				}
			}
		},
		'senddirect'(target) {
			this.sendDirect(target);
		},
		'h,help'(target) {
			switch (toID(target)) {
			case 'chal':
			case 'chall':
			case 'challenge':
				this.add('||/challenge - Open a prompt to challenge a user to a battle.');
				this.add('||/challenge [user] - Challenge the user [user] to a battle.');
				this.add('||/challenge [user], [format] - Challenge the user [user] to a battle in the specified [format].');
				this.add('||/challenge [user], [format] @@@ [rules] - Challenge the user [user] to a battle with custom rules.');
				this.add('||[rules] can be a comma-separated list of: [added rule], ![removed rule], -[banned thing], *[restricted thing], +[unbanned/unrestricted thing]');
				this.add('||If used in the DMs of a user, no [user] parameter can be used and it will challenge that user.');
				this.add('||/battlerules - Detailed information on what can go in [rules].');
				return;
			case 'accept':
				this.add('||/accept - Accept a challenge if only one is pending.');
				this.add('||/accept [user] - Accept a challenge from the specified user.');
				return;
			case 'reject':
				this.add('||/reject - Reject a challenge if only one is pending.');
				this.add('||/reject [user] - Reject a challenge from the specified user.');
				return;
			case 'user':
			case 'open':
				this.add('||/user [user] - Open a popup containing the user [user]\'s avatar, name, rank, and chatroom list.');
				return;
			case 'news':
				this.add('||/news - Opens a popup containing the news.');
				return;
			case 'ignore':
			case 'unignore':
				this.add('||/ignore [user] - Ignore all messages from the user [user].');
				this.add('||/unignore [user] - Remove the user [user] from your ignore list.');
				this.add('||/ignorelist - List all the users that you currently ignore.');
				this.add('||/clearignore - Remove all users on your ignore list.');
				this.add('||Note that staff messages cannot be ignored.');
				return;
			case 'nick':
				this.add('||/nick [new username] - Change your username.');
				return;
			case 'clear':
				this.add('||/clear - Clear the room\'s chat log.');
				return;
			case 'showdebug':
			case 'hidedebug':
				this.add('||/showdebug - Receive debug messages from battle events.');
				this.add('||/hidedebug - Ignore debug messages from battle events.');
				return;
			case 'showjoins':
			case 'hidejoins':
				this.add('||/showjoins [room] - Receive users\' join/leave messages.');
				this.add('||/hidejoins [room] - Ignore users\' join/leave messages.');
				this.add('||If no [room] is provided, changes the global setting.');
				return;
			case 'showbattles':
			case 'hidebattles':
				this.add('||/showbattles - Receive links to new battles in Lobby.');
				this.add('||/hidebattles - Ignore links to new battles in Lobby.');
				return;
			case 'ffto':
			case 'fastforwardto':
				this.add('||/ffto [turn] - Skip to turn [turn] in the current battle.');
				this.add('||/ffto +[turn] - Skip forward [turn] turns.');
				this.add('||/ffto -[turn] - Skip backward [turn] turns.');
				this.add('||/ffto 0 - Skip to the start of the battle.');
				this.add('||/ffto end - Skip to the end of the battle.');
				return;
			case 'unpackhidden':
			case 'packhidden':
				this.add('||/unpackhidden - Suppress hiding locked or banned users\' chat messages after the fact.');
				this.add('||/packhidden - Hide locked or banned users\' chat messages after the fact.');
				this.add('||Hidden messages from a user can be restored by clicking the button underneath their lock/ban reason.');
				return;
			case 'timestamps':
				this.add('||Set your timestamps preference:');
				this.add('||/timestamps [all|lobby|pms], [minutes|seconds|off]');
				this.add('||all - Change all timestamps preferences, lobby - Change only lobby chat preferences, pms - Change only PM preferences.');
				this.add('||off - Set timestamps off, minutes - Show timestamps of the form [hh:mm], seconds - Show timestamps of the form [hh:mm:ss].');
				return;
			case 'highlight':
			case 'hl':
				this.add('||Set up highlights:');
				this.add('||/highlight add [word 1], [word 2], [...] - Add the provided list of words to your highlight list.');
				this.add('||/highlight roomadd [word 1], [word 2], [...] - Add the provided list of words to the highlight list of whichever room you used the command in.');
				this.add('||/highlight list - List all words that currently highlight you.');
				this.add('||/highlight roomlist - List all words that currently highlight you in whichever room you used the command in.');
				this.add('||/highlight delete [word 1], [word 2], [...] - Delete the provided list of words from your entire highlight list.');
				this.add('||/highlight roomdelete [word 1], [word 2], [...] - Delete the provided list of words from the highlight list of whichever room you used the command in.');
				this.add('||/highlight clear - Clear your global highlight list.');
				this.add('||/highlight roomclear - Clear the highlight list of whichever room you used the command in.');
				this.add('||/highlight clearall - Clear your entire highlight list (all rooms and globally).');
				return;
			case 'rank':
			case 'ranking':
			case 'rating':
			case 'ladder':
				this.add('||/rank [user] - Shows all ladder ranks for the given [user].');
				this.add('||/rank [user], [format] - Shows the rank of [user] in the given [format].');
				this.add('||If no user is given, it defaults to the user of the command.');
				return;
			case 'afd':
				this.add('||/afd full - Enable all April Fools\' Day jokes.');
				this.add('||/afd sprites - Enable April Fools\' Day sprites.');
				this.add('||/afd default - Set April Fools\' Day to default (full on April 1st, off otherwise).');
				this.add('||/afd off - Disable April Fools\' Day jokes until the next refresh, and set /afd default.');
				this.add('||/afd never - Disable April Fools\' Day jokes permanently.');
				return;
			default:
				return true;
			}
		},
		'autojoin,cmd,crq,query'() {
			this.errorReply(`This is a PS system command; do not use it.`);
		},
	});
	clientCommands: ParsedClientCommands | null = null;
	currentElement: HTMLElement | null = null;
	/**
	 * Handles outgoing messages, like `/logout`. Return `true` to prevent
	 * the line from being sent to servers.
	 */
	handleSend(line: string, element = this.currentElement) {
		if (!line.startsWith('/') || line.startsWith('//')) return line;
		const spaceIndex = line.indexOf(' ');
		const cmd = (spaceIndex >= 0 ? line.slice(1, spaceIndex) : line.slice(1)) as 'parsed';
		const target = spaceIndex >= 0 ? line.slice(spaceIndex + 1).trim() : '';

		const cmdHandler = this.globalClientCommands[cmd] || this.clientCommands?.[cmd];
		if (!cmdHandler) return line;

		const previousElement = this.currentElement;
		this.currentElement = element;
		const cmdResult = cmdHandler.call(this, target, cmd, element);
		this.currentElement = previousElement;
		if (cmdResult === true) return line;
		return cmdResult || null;
	}
	send(msg: string | null, element?: HTMLElement | null) {
		if (!msg) return;
		msg = this.handleSend(msg, element);
		if (!msg) return;
		this.sendDirect(msg);
	}
	sendDirect(msg: string) {
		if (this.connectMode === 'deleted') {
			return this.errorReply(`This room has been deleted (you can't chat in it anymore)`);
		}
		if (this.connectMode === 'not-found') {
			return this.errorReply(`This room doesn't exist`);
		}
		PS.send(msg, this.id);
	}
	destroy() {
		if (this.connected) {
			this.sendDirect(`/noreply /leave ${this.id}`);
			this.connected = false;
		}
		for (let i = this.notifications.length - 1; i >= 0; i--) {
			this.dismissNotificationAt(i);
		}
	}
}

class PlaceholderRoom extends PSRoom {
	override readonly classType = 'placeholder';
	constructor(options: RoomOptions) {
		super(options);
		this.isPlaceholder = true;
	}
	override receiveLine(args: Args) {
		(this.backlog ||= []).push(args);
	}
}

/**********************************************************************
 * PS
 *********************************************************************/

type PSRoomPanelSubclass<T extends PSRoom = PSRoom> = (new () => PSRoomPanel<T>) & {
	readonly id: string,
	readonly routes: string[],
	/** optional Room class */
	readonly Model?: new (options: RoomOptions) => T,
	readonly location?: PSRoomLocation,
	/** do not put the roomid into the URL */
	noURL?: boolean,
	icon?: preact.ComponentChildren,
	title?: string,
	handleDrop?: (ev: DragEvent) => boolean | void,
};

/**
 * This model updates:
 * - when a room is joined or left
 * - changing which room is focused
 * - changing the width of the left room, in two-panel mode
 */
// cherish-party-banner:begin (patch-party-ui.py)
/**
 * 파티 초대 배너 — **어느 화면에 있든** 보이게 body 에 직접 붙인다.
 *
 * ⚠️ preact 트리(앱바·패널)에 끼우면 안 된다. panel-topbar 의 handleResize 가 유저바
 * 위치로 탭바 오른쪽 여백을 재는데, 그 트리에 뭘 넣으면 그 계산이 흔들린다.
 * ⚠️ 이름은 반드시 textContent 로 넣는다 (innerHTML 금지 — 표시명은 남이 정한 문자열이다).
 *
 * 홈 파티 섹션(panel-mainmenu renderCherishParty)과 **같은 상태**를 본다. 둘 다 뜨는 건
 * 의도다 — 배너는 「지금 알아채라」, 홈 카드는 「돌아와서 봐도 남아 있다」를 맡는다.
 */
let cherishInviteTimer: any = null;
function cherishPartyBannerRender() {
	const ID = 'cw-party-invite';
	const inv = (PS as any).cherishPartyInvite as
		{ from: string, fromName: string, until?: number } | null;
	const old = document.getElementById(ID);
	if (cherishInviteTimer) { clearTimeout(cherishInviteTimer); cherishInviteTimer = null; }
	// 만료된 초대는 없는 것과 같다 — 수락해 봐야 서버가 「받은 초대가 없어요」로 거절한다.
	if (inv?.until && inv.until <= Date.now()) {
		(PS as any).cherishPartyInvite = null;
		old?.remove();
		return;
	}
	if (!inv) { old?.remove(); return; }

	const el = old || document.createElement('div');
	if (!old) {
		el.id = ID;
		el.className = 'cw-party-invite';
		document.body.appendChild(el);
	}
	while (el.firstChild) el.removeChild(el.firstChild);

	const icon = document.createElement('span');
	icon.className = 'cw-pi-ic';
	icon.textContent = '\u{1F91D}';
	const text = document.createElement('span');
	text.className = 'cw-pi-text';
	const who = document.createElement('b');
	who.textContent = inv.fromName || inv.from;
	text.appendChild(who);
	text.appendChild(document.createTextNode(' 님이 파티에 불렀어요'));

	const act = (label: string, cls: string, cmd: string) => {
		const b = document.createElement('button');
		b.className = cls;
		b.textContent = label;
		b.onclick = () => {
			// 눌렀으면 바로 걷는다. 서버 응답을 기다리면 「눌렀는데 그대로」로 보인다.
			(PS as any).cherishPartyInvite = null;
			cherishPartyBannerRender();
			PS.send(cmd);
			PS.mainmenu.update(null);
		};
		return b;
	};

	const bar = document.createElement('span');
	bar.className = 'cw-pi-act';
	bar.appendChild(act('수락', 'cw-pi-yes', '/cherishparty accept'));
	bar.appendChild(act('거절', 'cw-pi-no', '/cherishparty decline'));

	el.appendChild(icon);
	el.appendChild(text);
	el.appendChild(bar);

	// 만료 시각에 스스로 걷는다 (서버 TTL 2분).
	if (inv.until) {
		const left = inv.until - Date.now();
		if (left > 0) cherishInviteTimer = setTimeout(cherishPartyBannerRender, left + 250);
	}
}
// cherish-party-banner:end
// cherish-maint:begin (patch-maint-ui.py)
/**
 * 배포 점검 오버레이 — **어느 화면에 있든** 보이게 body 에 직접 붙인다.
 *
 * ⚠️ preact 트리에 끼우면 안 된다 (panel-topbar 의 handleResize 가 그 트리 폭을 잰다).
 * ⚠️ 소켓이 끊겨도 이 DOM 은 남는다 — 그게 목적이다. 지금까지는 빨간 에러만 떴다.
 *
 * 상태의 원천 둘: 소켓 push(`|cherishmaint|`) 와 정적 파일 폴링(/cherish/maintenance.json).
 * 파일이 있으면 무조건 점검 중으로 본다 — 쇼다운도 api 도 죽은 창에서 유일하게 남는 신호다.
 */
interface CherishMaintState {
	phase: 'off' | 'notice' | 'active';
	until: number;
	eta: number;
}
const CHERISH_MAINT_URL = '/cherish/maintenance.json';
/** notice 가 끝나고도 이만큼 아무 신호가 없으면 스스로 걷는다 (점검 스크립트가 죽은 경우) */
const CHERISH_MAINT_NOTICE_GRACE = 30 * 1000;
let cherishMaint: CherishMaintState = { phase: 'off', until: 0, eta: 0 };
let cherishMaintTick: any = null;

function cherishMaintRender() {
	const ID = 'cw-maint';
	const old = document.getElementById(ID);
	if (cherishMaintTick) { clearInterval(cherishMaintTick); cherishMaintTick = null; }
	if (cherishMaint.phase === 'off') { old?.remove(); return; }

	const el = old || document.createElement('div');
	if (!old) {
		el.id = ID;
		el.className = 'cw-maint';
		document.body.appendChild(el);
	}
	while (el.firstChild) el.removeChild(el.firstChild);

	const card = document.createElement('div');
	card.className = 'cw-maint-card';

	const icon = document.createElement('div');
	icon.className = 'cw-maint-ic';
	icon.textContent = cherishMaint.phase === 'notice' ? '\u{23F3}' : '\u{1F6E0}\uFE0F';
	card.appendChild(icon);

	const title = document.createElement('div');
	title.className = 'cw-maint-title';
	card.appendChild(title);

	const body = document.createElement('div');
	body.className = 'cw-maint-body';
	card.appendChild(body);

	const paint = () => {
		if (cherishMaint.phase === 'notice') {
			const left = Math.max(0, Math.ceil((cherishMaint.until - Date.now()) / 1000));
			title.textContent = `${left}초 뒤 점검이 시작돼요`;
			body.textContent = '진행 중인 배틀은 여기서 멈춰요. ' +
				'이 판은 기록에 남지 않으니 전적도 점수도 그대로예요.';
		} else {
			title.textContent = '지금 점검 중이에요';
			const eta = cherishMaint.eta > 0 ? ` 예상 ${cherishMaint.eta}분쯤 걸려요.` : '';
			// ⚠️ 무엇을 올리는지는 말하지 않는다 — 미공개 기능이 새어나간다.
			body.textContent = '잠깐 손보는 중이에요. 끝나면 이 화면이 저절로 사라져요.' + eta;
		}
	};
	paint();
	if (cherishMaint.phase === 'notice') cherishMaintTick = setInterval(paint, 250);

	el.appendChild(card);
}

function cherishMaintApply(next: CherishMaintState) {
	const prev = cherishMaint.phase;
	cherishMaint = next;
	// 점검이 끝났으면 스스로 새로고침 — 배포로 js·css 스탬프가 바뀌었을 수 있다.
	// 페이지 로드 직후엔 prev 가 'off' 라 여기 안 걸린다 (새로고침 루프 방지).
	if (prev !== 'off' && next.phase === 'off') {
		cherishMaintRender();
		setTimeout(() => location.reload(), 400);
		return;
	}
	cherishMaintRender();
}

/**
 * ⚠️ **async/await 를 쓰지 않는다.** `.babelrc` 에 async 변환 플러그인이 없고 나머지는 전부
 * ES5 로 내려간다 (for-of 는 `assumeArray`, block-scoping 은 `throwIfClosureRequired`).
 * 이 파일에 async 선례가 0회인 것도 그래서다. `.then()` 체인으로 쓴다.
 */
function cherishMaintOff() {
	cherishMaintApply({ phase: 'off', until: 0, eta: 0 });
}
function cherishMaintPoll() {
	if (typeof fetch !== 'function') return;
	// ⚠️ 캐시버스터 없이는 CDN 이 물어 **해제가 안 보인다**.
	fetch(`${CHERISH_MAINT_URL}?t=${Date.now()}`, { cache: 'no-store' }).then(res => {
		if (res.ok) {
			return res.json().catch(() => ({})).then((data: any) => {
				cherishMaintApply({
					phase: 'active',
					until: 0,
					eta: Number(data && data.eta) > 0 ? Math.trunc(Number(data.eta)) : 0,
				});
			});
		}
		if (res.status !== 404) return; // 502 등은 판단 근거가 아니다 — 상태 유지
		if (cherishMaint.phase === 'active') {
			cherishMaintOff();
		} else if (cherishMaint.phase === 'notice' &&
			Date.now() > cherishMaint.until + CHERISH_MAINT_NOTICE_GRACE) {
			// 예고만 뜨고 점검이 안 왔다 — 스크립트가 중간에 죽은 경우. 모달을 걷는다.
			cherishMaintOff();
		}
	}).catch(() => {}); // 네트워크 실패는 상태를 바꾸지 않는다
}
setInterval(cherishMaintPoll, 30 * 1000);
cherishMaintPoll();
// cherish-maint:end
// cherish-team-voice-banner:begin (patch-team-voice-ui.py)
/**
 * 2대2 같은 편 통화방 배너 — 매칭 직후 15초만 산다.
 *
 * 서버가 `|cherishteamvoice|` 로 상태를 통째로 보낸다(client-main receive). 여기서는
 * 그 상태를 그리기만 한다 — 판단은 전부 서버·api 쪽이다.
 */
let cherishTeamVoiceTimer: any = null;
function cherishTeamVoiceRender() {
	const ID = 'cw-team-voice';
	const st = (PS as any).cherishTeamVoice as
		{ ask?: number, partner?: string, notice?: string, result?: string, until?: number } | null;
	const old = document.getElementById(ID);
	if (cherishTeamVoiceTimer) { clearTimeout(cherishTeamVoiceTimer); cherishTeamVoiceTimer = null; }
	if (!st) { old?.remove(); return; }
	if (st.until && st.until <= Date.now()) {
		// 시간이 다 됐다 — 서버도 같은 순간 「아니오」로 굳힌다.
		(PS as any).cherishTeamVoice = null;
		old?.remove();
		return;
	}

	const el = old || document.createElement('div');
	if (!old) {
		el.id = ID;
		el.className = 'cw-team-voice';
		document.body.appendChild(el);
	}
	while (el.firstChild) el.removeChild(el.firstChild);

	const icon = document.createElement('span');
	icon.className = 'cw-tv-ic';
	const text = document.createElement('span');
	text.className = 'cw-tv-text';

	if (st.ask) {
		icon.textContent = '\u{1F3A7}';
		const who = document.createElement('b');
		who.textContent = st.partner || '팀원';
		text.appendChild(who);
		text.appendChild(document.createTextNode(' 님과 같은 편이에요. 통화방에서 같이 할까요?'));
		const left = Math.max(0, Math.ceil(((st.until || 0) - Date.now()) / 1000));
		const clock = document.createElement('small');
		clock.className = 'cw-tv-left';
		clock.textContent = ` ${left}초`;
		text.appendChild(clock);

		const act = (label: string, cls: string, cmd: string, result: string) => {
			const b = document.createElement('button');
			b.className = cls;
			b.textContent = label;
			b.onclick = () => {
				// 눌렀으면 바로 화면을 바꾼다. 서버 응답을 기다리면 「눌렀는데 그대로」로 보인다.
				(PS as any).cherishTeamVoice = { result, until: Date.now() + 5000 };
				cherishTeamVoiceRender();
				PS.send(cmd);
			};
			return b;
		};
		const bar = document.createElement('span');
		bar.className = 'cw-tv-act';
		bar.appendChild(act('같이 갈래요', 'cw-tv-yes', '/cherishteamvoice yes', 'pending'));
		bar.appendChild(act('괜찮아요', 'cw-tv-no', '/cherishteamvoice no', 'declined'));
		el.appendChild(icon);
		el.appendChild(text);
		el.appendChild(bar);
		// 1초마다 남은 시간을 고쳐 그린다 (마지막엔 스스로 걷힌다).
		cherishTeamVoiceTimer = setTimeout(cherishTeamVoiceRender, 1000); // poll-ok: 남은 시간을 고쳐 그릴 뿐, 서버를 안 부른다
		return;
	}

	if (st.notice) {
		icon.textContent = '\u{1F507}';
		text.textContent = st.notice;
	} else if (st.result === 'pending') {
		icon.textContent = '\u{1F3A7}';
		text.textContent = '통화방을 만드는 중이에요.';
	} else if (st.result === 'ok') {
		icon.textContent = '\u{1F3A7}';
		text.textContent = '통화방으로 옮겼어요. 재밌게 하고 오세요!';
	} else if (st.result === 'declined') {
		icon.textContent = '\u{1F507}';
		text.textContent = '이번 판은 통화방 없이 갈게요.';
	} else {
		icon.textContent = '\u{1F507}';
		text.textContent = '통화방을 만들지 못했어요. 이번 판은 그대로 갈게요.';
	}
	el.appendChild(icon);
	el.appendChild(text);
	const left = (st.until || 0) - Date.now();
	if (left > 0) cherishTeamVoiceTimer = setTimeout(cherishTeamVoiceRender, left + 250);
}
// cherish-team-voice-banner:end
// cherish-milestone:begin (patch-milestone-popup.py)
/**
 * 배틀 10,000회 돌파 기념 팝업 — 한 기기에서 한 번, 어디든 누르면 닫힌다.
 * 베리 지급은 이것과 별개(서버 일괄 지급). 여기는 서버에 아무것도 묻지 않는다.
 */
const CHERISH_MILESTONE_KEY = 'cw-milestone-10000-seen';
const CHERISH_MILESTONE_IMG = '/cherish/ui/milestone-10000.webp?v=2';  // v2 2026-09-24: 원본 10001.png (날짜 줄 「오픈 후 19일만에 달성」)
/** 이 시각이 지나면 아무 일도 안 한다 (2026-10-08 00:00 KST) */
const CHERISH_MILESTONE_UNTIL = Date.UTC(2026, 9, 7, 15, 0, 0);

function cherishMilestoneShow() {
	if (Date.now() >= CHERISH_MILESTONE_UNTIL) return;
	try { if (localStorage.getItem(CHERISH_MILESTONE_KEY) === '1') return; } catch {}
	if (document.getElementById('cw-milestone')) return;

	const img = document.createElement('img');
	img.className = 'cw-milestone-img';
	img.alt = '배틀 10,000회 돌파 기념';
	img.onload = () => {
		// 점검 중이면 미룬다 — 끝나면 새로고침되며 다시 기회가 온다.
		if (document.getElementById('cw-maint')) return;
		try { localStorage.setItem(CHERISH_MILESTONE_KEY, '1'); } catch {}

		const el = document.createElement('div');
		el.id = 'cw-milestone';
		el.className = 'cw-milestone';
		el.setAttribute('role', 'dialog');
		el.setAttribute('aria-label', '배틀 10,000회 돌파 기념');

		const hint = document.createElement('div');
		hint.className = 'cw-milestone-hint';
		hint.textContent = '화면을 누르면 닫혀요';

		el.appendChild(img);
		el.appendChild(hint);

		let closed = false;
		const close = () => {
			if (closed) return;
			closed = true;
			document.removeEventListener('keydown', onKey);
			el.className = 'cw-milestone is-out';
			setTimeout(() => el.remove(), 200);
		};
		const onKey = (e: KeyboardEvent) => {
			if (e.key === 'Escape' || e.key === 'Enter' || e.key === ' ') close();
		};
		el.addEventListener('click', e => { e.preventDefault(); e.stopPropagation(); close(); });
		document.addEventListener('keydown', onKey);
		document.body.appendChild(el);
	};
	img.onerror = () => {}; // 그림이 없으면 조용히 안 뜬다
	img.src = CHERISH_MILESTONE_IMG;
}
setTimeout(cherishMilestoneShow, 800);
// cherish-milestone:end
// cherish-hide-seek:begin (patch-hide-seek.py)
/**
 * 출석 숨바꼭질 — 하루 첫 접속 때 내 아이 하나가 숨은 그림. 이름을 맞히면 베리.
 * 판정·보상은 전부 서버. 접속할 때 한 번 묻고, 힌트 그림은 서버가 준 시각표로 받아 간다.
 */
const CHERISH_HS_API = 'https://cherish-world.com/api/showdown/hideseek';

function cherishHsCall(path: string, body?: unknown): Promise<any> {
	const init: RequestInit = { credentials: 'include' };
	if (body !== undefined) {
		init.method = 'POST';
		init.headers = { 'Content-Type': 'application/json' };
		init.body = JSON.stringify(body);
	}
	return fetch(CHERISH_HS_API + path, init)
		.then(r => (r.ok ? r.json() : null))
		.then(j => (j ? (j.data !== undefined ? j.data : j) : null));
}

function cherishHsScene(stage: number): Promise<string | null> {
	return fetch(CHERISH_HS_API + '/scene/' + stage, { credentials: 'include', cache: 'no-store' })
		.then(r => (r.ok ? r.blob() : null))
		.then(b => (b ? URL.createObjectURL(b) : null))
		.catch(() => null);
}

function cherishHsEl(tag: string, cls: string, text?: string): HTMLElement {
	const el = document.createElement(tag);
	if (cls) el.className = cls;
	if (text !== undefined) el.textContent = text;
	return el;
}

function cherishHsWaitClear(fn: () => void) {
	if (document.getElementById('cw-hs')) return;
	if (document.getElementById('cw-milestone') || document.getElementById('cw-maint')) {
		// poll-ok: 겹치는 창이 사라지길 로컬로만 기다린다 (요청 없음)
		setTimeout(() => cherishHsWaitClear(fn), 1500);
		return;
	}
	fn();
}

function cherishHsOpen(view: any) {
	cherishHsScene(0).then(firstUrl => {
		if (!firstUrl) return;
		cherishHsWaitClear(() => cherishHsRender(view, firstUrl));
	});
}

function cherishHsRender(view: any, firstUrl: string) {
	const hintAt: number[] = Array.isArray(view.hintAtSec) ? view.hintAtSec : [];
	const shownAt = Date.now() - (Number(view.elapsedSec) || 0) * 1000;
	const base = Number(view.reward && view.reward.base) || 0;
	let solved = false;
	let closed = false;
	let busy = false;
	let tabHidden = false;
	let imgUrl = firstUrl;
	const timers: number[] = [];

	const root = cherishHsEl('div', 'cw-hs');
	root.id = 'cw-hs';
	const card = cherishHsEl('div', 'cw-hs-card');
	card.setAttribute('role', 'dialog');
	card.setAttribute('aria-label', '오늘의 숨바꼭질');

	const closeBtn = cherishHsEl('button', 'cw-hs-close', '✕') as HTMLButtonElement;
	closeBtn.type = 'button';
	closeBtn.setAttribute('aria-label', '닫기');
	const kicker = cherishHsEl('div', 'cw-hs-kicker', '오늘의 숨바꼭질');
	const title = cherishHsEl('div', 'cw-hs-title', '누가 숨어 있을까요?');
	const stage = cherishHsEl('div', 'cw-hs-stage');
	const img = document.createElement('img');
	img.className = 'cw-hs-img';
	img.alt = '숨어 있는 아이';
	img.src = firstUrl;
	const hint = cherishHsEl('div', 'cw-hs-hint', '기다리면 조금씩 더 보여요');
	stage.appendChild(img);
	stage.appendChild(hint);

	const desc = cherishHsEl('div', 'cw-hs-desc', '우리 아이 하나가 숨어 있어요. 아이의 이름을 불러주세요');
	const sub = cherishHsEl('div', 'cw-hs-sub', '이름을 안 지어준 아이라면 종족 이름으로 불러 주세요.');

	const form = cherishHsEl('form', 'cw-hs-form') as HTMLFormElement;
	const input = document.createElement('input');
	input.className = 'cw-hs-input';
	input.type = 'text';
	input.maxLength = 40;
	input.autocomplete = 'off';
	input.setAttribute('autocapitalize', 'off');
	input.setAttribute('spellcheck', 'false');
	input.placeholder = '이름';
	const go = cherishHsEl('button', 'cw-hs-go', '찾았다!') as HTMLButtonElement;
	go.type = 'submit';
	form.appendChild(input);
	form.appendChild(go);

	const msg = cherishHsEl('div', 'cw-hs-msg');
	const reward = cherishHsEl('div', 'cw-hs-reward',
		'맞히면 🫐 ' + base.toLocaleString('ko-KR') + ' · 빨리 찾으면 더 받아요');

	card.appendChild(closeBtn);
	card.appendChild(kicker);
	card.appendChild(title);
	card.appendChild(stage);
	card.appendChild(desc);
	card.appendChild(sub);
	card.appendChild(form);
	card.appendChild(msg);
	card.appendChild(reward);
	root.appendChild(card);

	const swapImg = (url: string) => {
		const old = imgUrl;
		imgUrl = url;
		img.src = url;
		if (old.startsWith('blob:')) setTimeout(() => URL.revokeObjectURL(old), 2000);
	};

	const close = () => {
		if (closed) return;
		closed = true;
		for (const t of timers) clearTimeout(t);
		document.removeEventListener('visibilitychange', onVis);
		root.className = 'cw-hs is-out';
		setTimeout(() => {
			root.remove();
			if (imgUrl.startsWith('blob:')) URL.revokeObjectURL(imgUrl);
		}, 200);
	};

	const onVis = () => {
		if (!document.hidden || solved || closed || tabHidden) return;
		tabHidden = true;
		cherishHsCall('/hidden', {}).catch(() => {});
	};

	// 힌트 — 서버가 준 시각표대로 다음 그림을 받는다. 풀리기 전이면 서버가 404 를 준다.
	hintAt.forEach((sec, stageNo) => {
		if (stageNo === 0) return;
		const wait = Math.max(0, sec * 1000 - (Date.now() - shownAt)) + 400;
		const fetchStage = (retry: boolean) => {
			if (solved || closed) return;
			cherishHsScene(stageNo).then(url => {
				if (solved || closed) {
					if (url) URL.revokeObjectURL(url);
					return;
				}
				if (url) {
					swapImg(url);
					hint.textContent = stageNo >= hintAt.length - 1 ? '거의 다 보여요' : '조금 더 보여요';
				} else if (retry) {
					// poll-ok: 서버 시계와 몇백 ms 어긋났을 때 한 번만 다시 받는다
					timers.push(window.setTimeout(() => fetchStage(false), 1500));
				}
			});
		};
		timers.push(window.setTimeout(() => fetchStage(true), wait));
	});

	const showSolved = (s: any) => {
		solved = true;
		for (const t of timers) clearTimeout(t);
		root.className = 'cw-hs is-found';
		kicker.textContent = '찾았다!';
		title.textContent = String(s.name || '');
		hint.remove();
		if (s.imageUrl) swapImg(String(s.imageUrl));
		img.alt = String(s.name || '');
		desc.textContent = String(s.reaction || '');
		sub.remove();
		msg.textContent = '';
		const got = Number(s.reward) || 0;
		const bonus = Number(s.bonus) || 0;
		reward.textContent = '🫐 ' + got.toLocaleString('ko-KR') + ' 베리를 받았어요';
		if (bonus > 0) {
			const extra = cherishHsEl('div', 'cw-hs-bonus',
				'빨리 찾아 줘서 🫐 ' + bonus.toLocaleString('ko-KR') + ' 더!');
			reward.appendChild(extra);
		}
		form.remove();
		const done = cherishHsEl('button', 'cw-hs-go cw-hs-done', '또 만나요') as HTMLButtonElement;
		done.type = 'button';
		done.addEventListener('click', close);
		card.appendChild(done);
		done.focus();
	};

	form.addEventListener('submit', e => {
		e.preventDefault();
		if (busy || solved) return;
		const text = input.value.trim();
		if (!text) {
			input.focus();
			return;
		}
		busy = true;
		go.disabled = true;
		msg.textContent = '';
		cherishHsCall('/guess', { text, tabHidden }).then(r => {
			if (!r) throw new Error('fail');
			if (r.result === 'correct') {
				showSolved(r.solved || {});
			} else if (r.result === 'wrong') {
				msg.textContent = '그 이름이 아닌가 봐요. 다시 불러 볼까요?';
				form.classList.remove('is-shake');
				void form.offsetWidth;
				form.classList.add('is-shake');
				input.select();
			} else if (r.result === 'already') {
				close();
			} else {
				msg.textContent = '잠시 뒤에 다시 해 주세요.';
			}
		}).catch(() => {
			msg.textContent = '잠시 뒤에 다시 해 주세요.';
		}).then(() => {
			busy = false;
			go.disabled = false;
		});
	});

	closeBtn.addEventListener('click', close);
	// 쇼다운 전역 단축키가 입력을 가로채지 않게 창 안의 키는 여기서 끝낸다
	root.addEventListener('keydown', e => {
		e.stopPropagation();
		if (e.key === 'Escape') close();
	});
	document.addEventListener('visibilitychange', onVis);
	document.body.appendChild(root);
	setTimeout(() => { if (!closed && !solved) input.focus(); }, 250);
}

function cherishHsStart() {
	cherishHsCall('').then(v => {
		if (v && v.state === 'open') cherishHsOpen(v);
	}).catch(() => {});
}
// poll-ok: 접속 뒤 한 번만 부른다 (주기 아님)
setTimeout(cherishHsStart, 1500);
// cherish-hide-seek:end
// cherish-notice-ticker:begin (patch-notice-ticker.py)
/**
 * 흐르는 공지 띠 — 관리자가 정한 시간 동안 화면 위쪽에 문구가 오른쪽에서 왼쪽으로 흐른다.
 * 데이터는 홈 응답(`/showdown/home` 의 notices)에 실려 cherishNoticeFeed 로 들어온다. 여기서는 서버에 묻지 않는다.
 * 배틀 방이 보이면 숨는다. 규칙은 patch-notice-ticker.py 머리 주석.
 */
type CherishNotice = { id: string, text: string, theme: string, startAt: string, endAt: string };
const CHERISH_NOTICE_DISMISS = 'cw-notice-dismissed';
/** 띠 높이(px) + 위아래 여백 — 방 내용을 이만큼 내린다. css 의 .cw-notice 높이와 짝. */
const CHERISH_NOTICE_PAD = 40;
/** 흐르는 속도 (px/초) */
const CHERISH_NOTICE_SPEED = 80;
let cherishNotices: CherishNotice[] = [];
let cherishNoticeHooked = false;
let cherishNoticeKey = '';
let cherishNoticePadded: Array<{ el: HTMLElement, prev: string }> = [];

function cherishNoticeDismissed(): string[] {
	try {
		const v = JSON.parse(localStorage.getItem(CHERISH_NOTICE_DISMISS) || '[]');
		return Array.isArray(v) ? v : [];
	} catch { return []; }
}
function cherishNoticeActive(): CherishNotice[] {
	const now = Date.now();
	const gone = cherishNoticeDismissed();
	return cherishNotices.filter(n => Date.parse(n.startAt) <= now && now < Date.parse(n.endAt) && gone.indexOf(n.id) < 0);
}
/** 지금 화면에 보이는 방들 — 두 칸 모드면 왼쪽·오른쪽, 아니면 한 칸. */
function cherishNoticeRooms(): any[] {
	const P: any = PS;
	const list = P.leftPanelWidth ? [P.leftPanel, P.rightPanel] : [P.panel];
	return list.filter((r: any) => !!r);
}
function cherishNoticeUnpad(keep?: HTMLElement[]) {
	cherishNoticePadded = cherishNoticePadded.filter(p => {
		if (keep && keep.indexOf(p.el) >= 0) return true;
		p.el.style.paddingTop = p.prev;
		return false;
	});
}
function cherishNoticeHide() {
	const bar = document.getElementById('cw-notice');
	if (bar) bar.remove();
	cherishNoticeUnpad();
	cherishNoticeKey = '';
}
function cherishNoticeBuild(bar: HTMLElement, list: CherishNotice[]) {
	bar.className = `cw-notice is-${list[0].theme || 'event'}`;
	bar.textContent = '';
	const tag = document.createElement('span');
	tag.className = 'cw-notice-tag';
	tag.textContent = '공지';
	const view = document.createElement('div');
	view.className = 'cw-notice-view';
	const track = document.createElement('div');
	track.className = 'cw-notice-track';
	list.forEach((n, i) => {
		if (i) {
			const sep = document.createElement('span');
			sep.className = 'cw-notice-sep';
			sep.textContent = '✦';
			track.appendChild(sep);
		}
		const item = document.createElement('span');
		item.className = 'cw-notice-item';
		item.textContent = n.text;
		track.appendChild(item);
	});
	view.appendChild(track);
	const x = document.createElement('button');
	x.className = 'cw-notice-x';
	x.type = 'button';
	x.setAttribute('aria-label', '공지 닫기');
	x.textContent = '×';
	x.addEventListener('click', e => {
		e.preventDefault();
		e.stopPropagation();
		const gone = cherishNoticeDismissed();
		list.forEach(n => { if (gone.indexOf(n.id) < 0) gone.push(n.id); });
		try { localStorage.setItem(CHERISH_NOTICE_DISMISS, JSON.stringify(gone.slice(-50))); } catch {}
		cherishNoticeRender();
	});
	bar.appendChild(tag);
	bar.appendChild(view);
	bar.appendChild(x);
	// 흐르는 시간은 실제 너비로 — 문구 길이와 상관없이 같은 속도로 흐른다.
	requestAnimationFrame(() => {
		const w = track.scrollWidth + view.clientWidth;
		track.style.animationDuration = `${Math.max(8, Math.round(w / CHERISH_NOTICE_SPEED))}s`;
	});
}
function cherishNoticeRender() {
	const list = cherishNoticeActive();
	const rooms = cherishNoticeRooms();
	const battle = rooms.some((r: any) => r.type === 'battle') || (PS as any).room?.type === 'battle';
	const els = rooms
		.map((r: any) => document.getElementById(`room-${r.id}`))
		.filter((el: any) => !!el) as HTMLElement[];
	if (!list.length || battle || !els.length || document.getElementById('cw-maint')) {
		cherishNoticeHide();
		return;
	}
	let bar = document.getElementById('cw-notice');
	if (!bar) {
		bar = document.createElement('div');
		bar.id = 'cw-notice';
		bar.setAttribute('role', 'status');
		document.body.appendChild(bar);
		cherishNoticeKey = '';
	}
	const key = list.map(n => `${n.id}:${n.theme}:${n.text}`).join('|');
	if (key !== cherishNoticeKey) {
		cherishNoticeKey = key;
		cherishNoticeBuild(bar, list);
	}
	// 자리 — 보이는 방들의 사각형 위쪽에 맞춘다(레이아웃마다 방 top 이 다르다).
	let top = Infinity, left = Infinity, right = -Infinity;
	els.forEach(el => {
		const r = el.getBoundingClientRect();
		top = Math.min(top, r.top);
		left = Math.min(left, r.left);
		right = Math.max(right, r.right);
	});
	bar.style.top = `${Math.round(top) + 6}px`;
	bar.style.left = `${Math.round(left) + 10}px`;
	bar.style.width = `${Math.max(0, Math.round(right - left) - 20)}px`;
	// 방 내용을 띠만큼 내린다. 다른 방으로 옮기면 옛 방은 원래 값으로.
	cherishNoticeUnpad(els);
	els.forEach(el => {
		if (cherishNoticePadded.some(p => p.el === el)) return;
		const base = parseFloat(getComputedStyle(el).paddingTop) || 0;
		cherishNoticePadded.push({ el, prev: el.style.paddingTop });
		el.style.paddingTop = `${base + CHERISH_NOTICE_PAD}px`;
	});
}
let cherishNoticeFrame = 0;
function cherishNoticeSchedule() {
	if (cherishNoticeFrame) return;
	cherishNoticeFrame = requestAnimationFrame(() => { cherishNoticeFrame = 0; cherishNoticeRender(); });
}
(window as any).cherishNoticeFeed = (notices: any) => {
	cherishNotices = Array.isArray(notices)
		? notices.filter((n: any) => n && typeof n.id === 'string' && typeof n.text === 'string')
		: [];
	if (!cherishNoticeHooked) {
		cherishNoticeHooked = true;
		PS.subscribe(() => cherishNoticeSchedule());
		window.addEventListener('resize', cherishNoticeSchedule);
		// 예약 시작·종료를 로컬 시계로 가른다 — 서버에 묻지 않는다. poll-ok
		setInterval(cherishNoticeSchedule, 15000);
	}
	cherishNoticeSchedule();
};
// cherish-notice-ticker:end
export const PS = new class extends PSModel {
	down: string | boolean = false;

	prefs = new PSPrefs();
	teams = new PSTeams();
	user = new PSUser();
	server = new PSServer();
	connection: PSConnection | null = null;
	/**
	 * While PS is technically disconnected while it's trying to connect,
	 * it still shows UI like it's connected, so you can click buttons
	 * before the server connection is established.
	 *
	 * `isOffline` is only set if PS is neither connected nor trying to
	 * connect.
	 */
	isOffline = false;
	readonly startTime = Date.now();

	router: PSRouter = null!;

	rooms: { [roomid: string]: PSRoom | undefined } = {};
	roomTypes: {
		[type: string]: PSRoomPanelSubclass | undefined,
	} = {};
	/**
	 * If a route starts with `*`, it's a cached room location for the room placeholder.
	 * Otherwise, it's a RoomType ID.
	 *
	 * Routes are filled in by `PS.updateRoomTypes()` and do not need to be manually
	 * filled.
	 */
	routes: Record<string, string> = Object.assign(Object.create(null), {
		// locations cached here because it needs to be guessed before roomTypes is filled in
		// this cache is optional, but prevents some flickering during loading
		// to update:
		// console.log('\t\t' + JSON.stringify(Object.fromEntries(Object.entries(PS.routes).filter(([k, v]) => k !== 'dm-*').map(([k, v]) => [k, '*' + (PS.roomTypes[v].location || '')]))).replaceAll(',', ',\n\t\t').replaceAll('":"', '": "').slice(1, -1) + ',')
		"news": "*mini-window",
		"": "*",
		"rooms": "*right",
		"user-*": "*popup",
		"viewuser-*": "*popup",
		"volume": "*popup",
		"options": "*modal-popup",
		"*": "*right",
		"battle-*": "*",
		"battles": "*right",
		"teamdropdown": "*modal-popup",
		"formatdropdown": "*modal-popup",
		"team-*": "*",
		"ladder": "*",
		"ladder-*": "*",
		"view-*": "*",
		"login": "*modal-popup",
		"help-*": "*right",
		"tourpopout": "*modal-popup",
		"groupchat-*": "*right",
		"users": "*popup",
		"useroptions-*": "*popup",
		"userlist": "*modal-popup",
		"changepassword": "*modal-popup",
		"forfeitbattle": "*modal-popup",
		"replaceplayer": "*modal-popup",
		"changebackground": "*modal-popup",
		"confirmleaveroom": "*modal-popup",
		"chatformatting": "*modal-popup",
		"popup-*": "*modal-popup",
		"roomtablist": "*modal-popup",
		"battleoptions": "*modal-popup",
		"battletimer": "*modal-popup",
		"rules-*": "*modal-popup",
		"game-*": "*",
	});
	/** List of rooms on the left side of the top tabbar */
	leftRoomList: RoomID[] = [];
	/** List of rooms on the right side of the top tabbar */
	rightRoomList: RoomID[] = [];
	/** List of mini-rooms in the Main Menu */
	miniRoomList: RoomID[] = [];
	/** Currently active popups, in stack order (bottom to top) */
	popups: RoomID[] = [];

	/**
	 * The currently focused room. Should always be the topmost popup
	 * if it exists. If no popups are open, it should be
	 * `PS.panel`.
	 *
	 * Determines which room receives keyboard shortcuts.
	 *
	 * Clicking inside a panel will focus it, in two-panel mode.
	 */
	room: PSRoom = null!;
	/**
	 * The currently active panel. Should always be either `PS.leftPanel`
	 * or `PS.leftPanel`. If no popups are open, should be `PS.room`.
	 *
	 * In one-panel mode, determines whether the left or right panel is
	 * visible. Otherwise, it just tracks which panel will be in focus
	 * after all popups are closed.
	 */
	panel: PSRoom = null!;
	/**
	 * Currently active left room.
	 *
	 * In two-panel mode, this will be the visible left panel.
	 *
	 * In one-panel mode, this is the visible room only if it is
	 * `PS.panel`. Still tracked when not visible, so we know which
	 * panels to display if PS is resized to two-panel mode.
	 */
	leftPanel: PSRoom = null!;
	/**
	 * Currently active right room.
	 *
	 * In two-panel mode, this will be the visible right panel.
	 *
	 * In one-panel mode, this is the visible room only if it is
	 * `PS.panel`. Still tracked when not visible, so we know which
	 * panels to display if PS is resized to two-panel mode.
	 */
	rightPanel: PSRoom | null = null;
	/**
	 * * 0 = only one panel visible
	 * * null = vertical nav layout
	 * n.b. Resizes only trigger a re-render if the panel layout or a
	 * width/height-dependent layout breakpoint changes.
	 */
	leftPanelWidth: number | null = 0;
	mainmenu: MainMenuRoom = null!;
	layoutViewportWidth = 0;

	roomLayoutBreakpointPassed(room: PSRoom, newWidth: number, newHeight: number) {
		const oldWidth = room.width;
		const oldHeight = room.height;
		if ((oldWidth < 550) !== (newWidth < 550) || // chat-room userlists, teambuilder
			(oldWidth < 620) !== (newWidth < 620) || // main menu and teambuilder tiny-layout class
			(oldWidth <= 780) !== (newWidth <= 780)) { // battle tiny-layout
			return true;
		}
		if (room.type === 'battle') {
			if ((oldWidth < 500) !==
				(newWidth < 500)) {
				return true;
			}
			const oldLayoutState = this.chooseBattleLayout(oldWidth, oldHeight, this.prefs.battlelayout);
			const newLayoutState = this.chooseBattleLayout(newWidth, newHeight, this.prefs.battlelayout);
			if (oldLayoutState.layout !== newLayoutState.layout ||
				oldLayoutState.battleHeight !== newLayoutState.battleHeight ||
				oldLayoutState.battleWidth !== newLayoutState.battleWidth ||
				oldLayoutState.overlayControls !== newLayoutState.overlayControls) {
				return true;
			}
		}
		return false;
	}
	chooseBattleLayout(width: number, height: number, preference?: BattleLayoutPreference | null) {
		let scale = Math.min(1, width / 640, height / 360);
		const uncappedBattleHeight = Math.round(360 * scale);
		// cherish-world: 기본 = 배틀 위 + 로그 아래 (side-by-side 는 옵션 선택 시에만)
		let layout: BattlePanelLayout =
			height < uncappedBattleHeight + 150 ? 'scrolling' : 'top-and-bottom';

		const preferredLayout = preference?.replace(/-overlay$/, '') as BattlePanelLayout;
		if (preferredLayout) {
			layout = preferredLayout;
		}
		if (layout === 'side-by-side' && width >= 500) {
			scale = Math.min(scale, Math.max(0, width - 180) / 640);
		} else if (layout === 'top-and-bottom') {
			// cherish-world: 1배 캡 해제 — 가로 폭에 맞춰 도트 확대 (세로 60% 상한)
			scale = Math.min(width / 640, (height * 0.6) / 360);
		}

		const battleHeight = Math.round(360 * scale);
		const battleWidth = Math.round(640 * scale);
		let overlayControls = height < battleHeight + 180;
		if (preferredLayout) {
			overlayControls = !!preference?.endsWith('-overlay');
		}
		return { layout, battleHeight, battleWidth, overlayControls };
	}

	/**
	 * The drag-and-drop API is incredibly dumb and doesn't let us know
	 * what's being dragged until the `drop` event, so we track what we
	 * do know here.
	 *
	 * Note that `PS.dragging` will sometimes have type `?` if the drag
	 * was initiated outside PS (e.g. dragging a team from File Explorer
	 * to PS), and for security reasons it's impossible to know what
	 * they are until they're dropped.
	 */
	dragging: { type: 'room', roomid: RoomID, foreground?: boolean } |
		{ type: 'team', team: Team | number, folder: string | null } |
		{ type: '?' } | // browser preventing us from knowing what's being dragged
		null = null;
	lastMessageTime = '';

	/** Tracks whether or not to display the "Use arrow keys" hint */
	arrowKeysUsed = false;

	newsHTML = document.querySelector('#room-news .readable-bg')?.innerHTML || '';
	newsId = document.getElementById('room-news')?.getAttribute('data-newsid') || null;

	libsLoaded = makeLoadTracker();

	constructor() {
		super();

		this.mainmenu = this.addRoom({
			id: '' as RoomID,
			title: "홈",
		}) as MainMenuRoom;

		// cherish-world: 우측 패널 폐지 (rooms 방 생성 안 함)
		this.rightPanel = null!;

		if (false && this.newsHTML) { // cherish-world: 뉴스/리라이트 베타 미니창 자동 오픈 비활성
			this.addRoom({
				id: 'news' as RoomID,
				title: "News",
				autofocus: false,
			});
		}

		// Create rooms before /autojoin is sent to the server
		let autojoin = this.prefs.autojoin;
		if (autojoin) {
			if (typeof autojoin === 'string') {
				autojoin = { showdown: autojoin };
			}
			let rooms = autojoin[this.server.id] || '';
			for (let title of rooms.split(",")) {
				const id = /[^a-z0-9-]/.test(title) ? toID(title) as any as RoomID : title as RoomID;
				this.addRoom({ id, title, connected: 'pending', autofocus: false });
			}
		}

		// for old versions of Safari
		if (window.webkitNotification) {
			window.Notification ||= window.webkitNotification;
		}

		this.updateLayout();
		window.addEventListener('resize', () => {
			// super.update() skips another updateLayout() call
			if (this.updateLayout()) super.update();
		});
	}

	// Panel layout
	///////////////
	/**
	 * "minWidth" and "maxWidth" are a bit deceptive here - to be clear,
	 * all PS rooms are expected to responsively support any width from
	 * 320px up, when in single panel mode. These metrics are used purely
	 * to calculate the location of the separator in two-panel mode.
	 *
	 * - `minWidth` - minimum width as a right-panel
	 * - `width` - preferred width, minimum width as a left-panel
	 * - `maxWidth` - maximum width as a left-panel
	 *
	 * PS will only show two panels if it can fit `width` in the left, and
	 * `minWidth` in the right. Extra space will be given to to right panel
	 * until it reaches `width`, then evenly distributed until both panels
	 * reach `maxWidth`, and extra space above that will be given to the
	 * right panel.
	 */
	getWidthFor(room: PSRoom) {
		switch (room.type) {
		case 'mainmenu':
			return {
				minWidth: 340,
				width: 628,
				maxWidth: 628,
				isMainMenu: true,
			};
		case 'chat':
		case 'rooms':
		case 'battles':
			return {
				minWidth: 320,
				width: 570,
				maxWidth: 640,
			};
		case 'team':
		case 'teambuilder':
			return {
				minWidth: 660,
				width: 660,
				maxWidth: 660,
			};
		case 'battle': {
			const sideBySide = !this.prefs.battlelayout ||
				this.prefs.battlelayout === 'side-by-side' || this.prefs.battlelayout === 'side-by-side-overlay';
			return {
				minWidth: 320,
				width: sideBySide ? 956 : 640,
				maxWidth: sideBySide ? 1180 : 640,
			};
		}
		}
		return {
			minWidth: 640,
			width: 640,
			maxWidth: 640,
		};
	}
	/** @returns changed */
	updateLayout(): boolean {
		const leftPanelWidth = this.calculateLeftPanelWidth();
		// `window.inner*` subtracts on-screen keyboards, but `document.body.offset*` doesn't
		// we don't want the layout to jump around wildly when we open an OSK, so...
		// `document.body.offset*` is the scrollable area, though, we want
		// `document.documentElement.client*` for the viewport dimensions before OSK
		const viewportWidth = document.documentElement.clientWidth;
		const viewportHeight = document.documentElement.clientHeight;
		const roomHeight = viewportHeight - 56;
		let needsUpdate = this.leftPanelWidth !== leftPanelWidth;
		if (leftPanelWidth === null) {
			const headerWidthOffset = viewportWidth <= 700 ? 0 : VERTICAL_HEADER_WIDTH;
			const roomWidth = viewportWidth + 1 - headerWidthOffset;
			needsUpdate ||= this.roomLayoutBreakpointPassed(this.panel, roomWidth, viewportHeight - 30);
			this.panel.width = roomWidth;
			this.panel.height = viewportHeight - 30;
		} else if (leftPanelWidth) {
			const rightPanelWidth = viewportWidth + 1 - leftPanelWidth;
			needsUpdate ||= this.roomLayoutBreakpointPassed(this.leftPanel, leftPanelWidth, roomHeight);
			needsUpdate ||= this.roomLayoutBreakpointPassed(this.rightPanel!, rightPanelWidth, roomHeight);
			this.leftPanel.width = leftPanelWidth;
			this.leftPanel.height = roomHeight;
			this.rightPanel!.width = rightPanelWidth;
			this.rightPanel!.height = roomHeight;
		} else {
			needsUpdate ||= this.roomLayoutBreakpointPassed(this.panel, viewportWidth, roomHeight);
			this.panel.width = viewportWidth;
			this.panel.height = roomHeight;
		}

		if (this.leftPanelWidth !== leftPanelWidth) {
			this.leftPanelWidth = leftPanelWidth;
		}
		this.layoutViewportWidth = viewportWidth;
		(this.panel as ChatRoom).log?.updateScroll();
		if (this.leftPanelWidth) {
			(this.leftPanel as ChatRoom).log?.updateScroll();
			(this.rightPanel as ChatRoom).log?.updateScroll();
		}
		return needsUpdate;
	}
	getRoom(elem: HTMLElement | EventTarget | null | undefined, skipClickable?: boolean): PSRoom | null {
		let curElem: HTMLElement | null = elem as HTMLElement;
		// might be the close button on the roomtab
		if ((curElem as HTMLButtonElement)?.name === 'closeRoom' && (curElem as HTMLButtonElement).value) {
			return PS.rooms[(curElem as HTMLButtonElement).value] || null;
		}
		while (curElem) {
			if (curElem.id.startsWith('room-')) {
				return PS.rooms[curElem.id.slice(5)] || null;
			}
			if (curElem.getAttribute('data-roomid')) {
				return PS.rooms[curElem.getAttribute('data-roomid') as RoomID] || null;
			}
			if (skipClickable && (
				curElem.tagName === 'A' || curElem.tagName === 'BUTTON' || curElem.tagName === 'INPUT' ||
				curElem.tagName === 'SELECT' || curElem.tagName === 'TEXTAREA' || curElem.tagName === 'LABEL' ||
				curElem.classList?.contains('textbox') || curElem.classList?.contains('username')
			)) {
				return null;
			}
			curElem = curElem.parentElement;
		}
		return null;
	}
	dragOnto(fromRoom: PSRoom, toLocation: 'left' | 'right' | 'mini-window', toIndex: number) {
		// one day you will be able to rearrange mainmenu and rooms, but not today
		if (fromRoom.id === '' || fromRoom.id === 'rooms') return;

		const onHome = (toLocation === 'left' && toIndex === 0);

		PS.moveRoom(fromRoom, toLocation, onHome, toIndex);
		PS.update();
	}
	override update() {
		this.updateLayout();
		super.update();
	}
	receive(msg: string) {
		msg = msg.endsWith('\n') ? msg.slice(0, -1) : msg;
		let roomid = '' as RoomID;
		if (msg.startsWith('>')) {
			const nlIndex = msg.indexOf('\n');
			roomid = msg.slice(1, nlIndex) as RoomID;
			msg = msg.slice(nlIndex + 1);
		}
		// cherish-extraction-embed:begin (patch-extraction-embed.py)
		// 같은 계정의 일반 play 탭에는 익스트랙션 배틀을 자동으로 열지 않는다.
		if (roomid.startsWith('battle-gen9cherishextraction-') &&
			!cherishExtractionEmbed && cherishExtractionEntryRoom !== roomid &&
			window.location.pathname !== `/${roomid}` && window.location.hash !== `#${roomid}`) return;
		// cherish-extraction-embed:end
		// cherish-extraction-audio-room:begin
		if (cherishExtractionEmbed && roomid.startsWith('battle-') &&
			!roomid.startsWith('battle-gen9cherishextraction-')) return;
		// cherish-extraction-audio-room:end
		const roomid2 = roomid || 'lobby' as RoomID;
		let room = PS.rooms[roomid];
		console.log('\u2705 ' + (roomid ? '[' + roomid + '] ' : '') + '%c' + msg, "color: #007700");
		let isInit = false;
		for (const line of msg.split('\n')) {
			const args = BattleTextParser.parseLine(line);
			switch (args[0]) {
			// cherish-party-recv:begin (patch-party-ui.py)
			// 파티 상태 push — 폴링이면 초대가 늦게 떠서 「눌렀는데 반응 없음」이 된다.
			// 파티가 바뀔 때마다 서버가 양쪽에 보낸다 (chat-plugins/cherish-party.ts).
			case 'cherishparty': {
				try {
					const data = JSON.parse(args[1]);
					(PS as any).cherishParty = data;
					// 받은 초대의 진실은 **서버 상태**다 (patch-party.py v2 의 invitedFrom).
					// push 를 놓친 사람이 여기서 되찾고, 만료·수락·거절도 여기서 함께 걷힌다.
					// ⚠️ 옛 서버 호환 — 키 자체가 없으면 손대지 않는다. 키가 있고 값이 null 일
					// 때만 지운다 (클라가 서버보다 먼저 반영되는 창이 있다).
					if (data.partner) {
						(PS as any).cherishPartyInvite = null;
					} else if ('invitedFrom' in data) {
						(PS as any).cherishPartyInvite = data.invitedFrom ? {
							from: data.invitedFrom,
							fromName: data.invitedFromName || data.invitedFrom,
							until: Date.now() + (Number(data.invitedFromLeft) || 0),
						} : null;
					}
				} catch {}
				cherishPartyBannerRender();
				PS.mainmenu.update(null);
				continue;
			}
			case 'cherishpartyinvite': {
				try {
					const inv = JSON.parse(args[1]);
					// 서버 TTL 2분. 상태 push 가 남은 시간을 더 정확히 덮어쓴다.
					inv.until = Date.now() + 2 * 60 * 1000;
					(PS as any).cherishPartyInvite = inv;
					// 홈 밖(배틀 방·도감 등)에서도 알아채게. 음소거·볼륨 설정은 이 함수가 본다.
					PS.playNotificationSound();
				} catch {}
				cherishPartyBannerRender();
				PS.mainmenu.update(null);
				continue;
			}
			case 'cherishmaze': {
				// cherish-world fork: 구름 미궁 파티 동행자가 뭔가 골랐다 (patch-party.py 의 `mazeping`).
				// 내용은 안 온다 — 열려 있는 미궁 방이 제 상태를 **한 번** 다시 받는다.
				// 이 신호가 배틀 껍데기의 3초 폴링을 대신한다 (2026-09-23, §4-6 「통로 하나」).
				try { (window as any).cherishMazeHooks?.forEach((fn: any) => fn()); } catch {}
				continue;
			}
			// cherish-party-recv:end
			// cherish-maint-recv:begin (patch-maint-ui.py)
			// 점검 신호 push (chat-plugins/cherish-maintenance.ts). 폴링(30초)보다 빠르고,
			// 예고 카운트다운은 이 경로로만 온다 — 예고 시점엔 아직 정적 파일이 없다.
			case 'cherishmaint': {
				try {
					const data = JSON.parse(args[1]);
					const phase = data?.phase === 'notice' || data?.phase === 'active' ? data.phase : 'off';
					// 배틀 중이거나 다른 탭을 보고 있어도 알아채게. 음소거·볼륨 설정은 이 함수가 본다.
					if (phase === 'notice' && cherishMaint.phase === 'off') PS.playNotificationSound();
					cherishMaintApply({
						phase,
						until: Number(data?.until) || 0,
						eta: Number(data?.eta) > 0 ? Math.trunc(Number(data.eta)) : 0,
					});
				} catch {}
				continue;
			}
			// cherish-maint-recv:end
			// cherish-match-sound-recv:begin (patch-match-sound.py)
			// 자동매칭으로 판이 잡혔다 (서버 ladders.ts cherishNotifyMatched).
			// 파티 동행자에게도 온다 — 1대1 은 출전자 명의로만 큐에 서기 때문에(교대) 쉬는 쪽은
			// 자기 |updatesearch| 로는 판이 잡힌 걸 알 길이 없다.
			// 대기방·도전은 서버가 이 줄을 안 보낸다 (그 경로는 cherishNoteMatched 를 안 탄다).
			case 'cherishmatched': {
				PS.playCherishMatchSound();
				continue;
			}
			// cherish-match-sound-recv:end
			// cherish-team-voice-recv:begin (patch-team-voice-ui.py)
			// 2대2 같은 편 통화방 — 매칭 확정 직후 서버가 밀어 준다 (server/cherish-team-voice.ts).
			case 'cherishteamvoice': {
				try {
					const data = JSON.parse(args[1]);
					if (data.ask) {
						const seconds = Number(data.seconds) || 15;
						(PS as any).cherishTeamVoice = {
							ask: 1, partner: data.partner || '', until: Date.now() + seconds * 1000,
						};
						// 홈 밖(팀 편집·도감)에서도 알아채게. 음소거·볼륨 설정은 이 함수가 본다.
						PS.playNotificationSound();
					} else if (data.notice) {
						(PS as any).cherishTeamVoice = { notice: String(data.notice), until: Date.now() + 8000 };
					} else if (data.result) {
						// 「만드는 중」은 결과가 아니라 경유지라 더 오래 둔다.
						const ms = data.result === 'pending' ? 20000 : 5000;
						(PS as any).cherishTeamVoice = { result: String(data.result), until: Date.now() + ms };
					}
				} catch {}
				cherishTeamVoiceRender();
				continue;
			}
			// cherish-team-voice-recv:end
			case 'init': {
				isInit = true;
				room = PS.rooms[roomid2];
				const [, type] = args;
				if (!room) {
					room = this.addRoom({
						id: roomid2,
						type,
						connected: 'init',
						// cherish-one-battle-tab-autofocus:begin (patch-one-battle-tab.py)
						// 되살아나는 방(5초 안에 닫은 배틀 방)은 포커스를 뺏지 않는다 — 뺏으면 도로 닫을 때 홈으로 떨어진다.
						autofocus: roomid !== 'staff' && roomid !== 'upperstaff' && !PS.cherishRecentlyLeftBattle(roomid2),
						// cherish-one-battle-tab-autofocus:end
						// probably the only use for `autoclosePopups: false`.
						// (the server sometimes sends a popup error message and a new room at the same time)
						autoclosePopups: false,
					});
					if (room && type === 'battle') {
						(room as BattleRoom).rejoining = msg.includes('\n|start\n');
					}
				} else {
					room.type = type;
					room.connected = 'init';
					this.updateRoomTypes();
				}
				// cherish-one-battle-tab-init:begin (patch-one-battle-tab.py)
				// 배틀 탭은 하나만 — 이 방 말고 다른 배틀 탭을 닫는다. 재연결 init 은 뺀다(열린 방이 전부 다시 init 을 받는다).
				if (room && type === 'battle' && room.connectMode !== 'pending-reconnect') PS.cherishOneBattleTab(room);
				// cherish-one-battle-tab-init:end
				if (room) {
					if (room.connectMode === 'pending-reconnect') {
						room.connectMode = 'normal';
						// cherish-send-hold-init:begin (patch-reconnect-hold.py)
						// 재연결 동안 붙잡아 둔 이 방 메시지를 init 묶음 처리가 끝난 뒤에 내보낸다.
						setTimeout(() => PS.connection?.cherishReleaseHeld(), 0);
						// cherish-send-hold-init:end
						if (room.handleReconnect(msg)) {
							room.connected = true;
							return;
						}
					}
					room.connected = true;
				}
				this.updateAutojoin();
				this.update();
				continue;
			} case 'deinit': {
				room = PS.rooms[roomid2];
				if (room) {
					room.connected = false;
					if (room.connectMode !== 'deleted') this.removeRoom(room);
					this.updateAutojoin();
					this.update();
				}
				continue;
			} case 'noinit': {
				room = PS.rooms[roomid2];
				const roomPreviouslyConnected = room?.connected === true || room?.connectMode === 'pending-reconnect';
				if (!room && roomid2.startsWith('battle-') && args[1] === 'nonexistent') {
					// possible replay; init a battle room for it
					room = this.addRoom({
						id: roomid2,
						type: 'battle',
						connectMode: null,
					});
					(room as BattleRoom).rejoining = false;
				}
				if (room) {
					room.connected = false;
					if (args[1] === 'namerequired') {
						room.connectMode = 'pending-login';
						if (!PS.user.initializing) {
							room.receiveLine(['error', args[2]]);
						}
					} else if (args[1] === 'nonexistent' || args[1] === 'joinfailed') {
						// sometimes we assume a room is a chatroom when it's not
						// when that happens, just ignore this error
						if (room.type === 'chat' || room.type === 'battle') {
							room.connectMode = roomPreviouslyConnected ? 'deleted' : 'not-found';
							room.receiveLine(args);
						}
					} else if (args[1] === 'rename') {
						room.connected = true;
						room.connectMode = 'normal';
						room.title = args[3] || room.title;
						this.renameRoom(room, args[2] as RoomID);
					}
				}
				this.update();
				continue;
			} case 'nametaken': {
				PS.join('login' as RoomID, { args: { error: `Someone is already using the name ${args[1]}.` } });
				break;
			} case 'chat': case 'c': {
				if (args[1] === '~' && (args[2] + ' ').startsWith('/warn ')) {
					PS.join(`rules-warn` as RoomID, {
						args: {
							type: 'warn',
							message: args[2].slice(6).trim() || undefined,
						},
						parentElem: null,
					});
					continue;
				}
			}

			}
			room?.receiveLine(args);
		}
		if (room && isInit) room.connected = true;
		room?.update(isInit ? [`initdone`] : null);
	}
	send(msg: string, roomid?: RoomID) {
		const bracketRoomid = roomid ? `[${roomid}] ` : '';
		console.log(`\u25b6\ufe0f ${bracketRoomid}%c${msg}`, "color: #776677");
		if (!this.connection) {
			PS.alert(`You are not connected and cannot send ${msg}.`);
			return;
		}
		this.connection.send(`${roomid || ''}|${msg}`);
	}
	isVisible(room: PSRoom): boolean {
		if (PS.isPanel(room)) {
			return !this.leftPanelWidth ? room === this.panel : room === this.leftPanel || room === this.rightPanel;
		}
		if (room.location === 'mini-window') {
			return !this.leftPanelWidth ? this.mainmenu === this.panel : this.mainmenu === this.leftPanel;
		}
		// some kind of popup
		return true;
	}
	isVisiblePanel(room: PSRoom) {
		if (!this.leftPanelWidth) {
			// one panel visible
			return room === this.panel || room === this.room;
		} else {
			// both panels visible
			return room === this.rightPanel || room === this.leftPanel || room === this.room;
		}
	}
	/**
	 * @see {@link leftPanelWidth} for return value meaning
	 */
	calculateLeftPanelWidth() {
		// cherish-world: 항상 단일 패널 — 접속 유저 보드·팀 편성은 홈에 통합 (2026-08-31)
		return 0;
		if (this.prefs.onepanel === 'vertical') return null;
		if (!this.prefs.onepanel && document.documentElement.clientWidth < 700) return null;
		if (!this.prefs.onepanel && document.documentElement.clientHeight < 430) return null;
		// If we don't have both a left room and a right room, obviously
		// just show one room
		if (!this.leftPanel || !this.rightPanel || this.prefs.onepanel) {
			return 0;
		}

		// The rest of this code can assume we have both a left room and a
		// right room, and also want to show both if they fit

		const available = document.documentElement.clientWidth;
		const left = this.getWidthFor(this.leftPanel);
		const right = this.getWidthFor(this.rightPanel);

		let excess = available - (left.width + right.width);
		if (excess >= 0) {
			// both fit in full size
			const leftStretch = left.maxWidth - left.width;
			if (!leftStretch) return left.width;
			const rightStretch = right.maxWidth - right.width;
			if (leftStretch + rightStretch >= excess) return left.maxWidth;
			// evenly distribute the excess
			return left.width + Math.floor(excess * leftStretch / (leftStretch + rightStretch));
		}

		if (left.isMainMenu) {
			if (available >= left.minWidth + right.width) {
				return left.minWidth;
			}
			return 0;
		}

		if (available >= left.width + right.minWidth) {
			return left.width;
		}
		return 0;
	}
	createRoom(options: RoomOptions) {
		options.location ||= this.getRouteLocation(options.id);
		options.location = this.normalizeRoomLocation(options.location);
		options.type ||= this.getRoute(options.id) || '';
		const RoomType = this.roomTypes[options.type];
		options.noURL ??= RoomType?.noURL;
		if (RoomType?.title) options.title = RoomType.title;
		const Model = RoomType ? (RoomType.Model || PSRoom) : PlaceholderRoom;
		const room = new Model(options);
		room.location = this.normalizeRoomLocation(room.location);
		return room;
	}
	normalizeRoomLocation(location: string): PSRoomLocation;
	normalizeRoomLocation(location: string | undefined): PSRoomLocation | undefined;
	normalizeRoomLocation(location: any): any {
		if (!location) return location;
		switch (location) {
		case 'left': case 'right': case 'popup': case 'mini-window': case 'modal-popup':
			return location;
		default:
			return 'modal-popup';
		}
	}
	getRouteInfo(roomid: RoomID) {
		if (this.routes[roomid]) return this.routes[roomid];
		const hyphenIndex = roomid.indexOf('-');
		if (hyphenIndex < 0) return this.routes['*'] || null;
		roomid = roomid.slice(0, hyphenIndex) + '-*' as RoomID;
		if (this.routes[roomid]) return this.routes[roomid];
		return null;
	}
	getRouteLocation(roomid: RoomID): PSRoomLocation {
		// must be hardcoded here to have a different loc while also being a ChatRoom
		if (roomid.startsWith('dm-')) {
			if (document.documentElement.clientWidth <= 818) {
				return 'left';
			}
			return 'mini-window';
		}
		const routeInfo = this.getRouteInfo(roomid);
		if (!routeInfo) return 'left';
		if (routeInfo.startsWith('*')) {
			return this.normalizeRoomLocation(routeInfo.slice(1));
		}
		return this.normalizeRoomLocation(PS.roomTypes[routeInfo]!.location) || 'left';
	}
	getRoute(roomid: RoomID) {
		const routeInfo = this.getRouteInfo(roomid);
		return routeInfo?.startsWith('*') ? null : routeInfo || null;
	}
	addRoomType(...types: PSRoomPanelSubclass[]) {
		for (const RoomType of types) {
			this.roomTypes[RoomType.id] = RoomType;
			for (const route of RoomType.routes) {
				this.routes[route] = RoomType.id;
			}
		}
		this.updateRoomTypes();
	}
	updateRoomTypes() {
		let updated = false;
		for (const roomid in this.rooms) {
			const room = this.rooms[roomid]!;
			const typeIsGuessed = room.type === this.routes['*'] && !roomid.includes('-');
			if (!room.isPlaceholder && !typeIsGuessed) continue;

			let type = (!typeIsGuessed && room.type) || this.getRoute(roomid as RoomID) || room.type || '';
			if (!room.isPlaceholder && type === room.type) continue;

			const RoomType = type && this.roomTypes[type];
			if (!RoomType) continue;

			const options: RoomOptions = room;
			if (RoomType.title) options.title = RoomType.title;
			options.type = type;
			options.connectMode = undefined;
			const Model = RoomType.Model || PSRoom;
			const newRoom = new Model(options);
			this.rooms[roomid] = newRoom;
			if (this.leftPanel === room) this.leftPanel = newRoom;
			if (this.rightPanel === room) this.rightPanel = newRoom;
			if (this.panel === room) this.panel = newRoom;
			if (roomid === '') this.mainmenu = newRoom as MainMenuRoom;
			if (this.room === room) {
				this.room = newRoom;
				newRoom.focusNextUpdate = { preventScroll: true };
			}

			updated = true;
		}
		if (updated) this.update();
	}
	setFocus(room: PSRoom, options?: PSRoomFocusOptions) {
		room.onRequestFocus?.(options);
	}
	focusRoom(roomid: RoomID) {
		const room = this.rooms[roomid];
		if (!room) return false;
		if (this.room === room) {
			const focusOptions = room.focusNextUpdate === true ? undefined : room.focusNextUpdate || undefined;
			room.focusNextUpdate = false;
			this.setFocus(room, focusOptions);
			return true;
		}
		this.closePopupsAbove(room, true);
		if (!this.isVisiblePanel(room)) {
			room.focusNextUpdate = true;
		}
		if (PS.isPanel(room)) {
			if (room.location === 'right') {
				this.rightPanel = room;
			} else {
				this.leftPanel = room;
			}
			this.panel = this.room = room;
		} else { // popup or mini-window
			if (room.location === 'mini-window') {
				this.leftPanel = this.panel = PS.mainmenu;
			}
			this.room = room;
		}
		this.room.autoDismissNotifications();
		this.update();
		this.setFocus(room);
		return true;
	}
	horizontalNav(room = this.room) {
		if (this.leftPanelWidth === null) {
			return { rooms: [], index: -1 };
		}
		const rooms = this.leftRoomList.concat(this.rightRoomList);
		const miniRoom = this.miniRoomList[0] !== 'news' ? this.miniRoomList[0] : null;
		if (miniRoom) rooms.splice(1, 0, miniRoom);
		const roomid = room.location === 'mini-window' ? (miniRoom || '' as RoomID) : room.id;

		const index = rooms.indexOf(roomid);
		// index === -1: popup or something
		return { rooms, index };
	}
	verticalNav(room = this.room) {
		if (this.leftPanelWidth === null) {
			return this.fullNav(room);
		}
		if (room.location !== 'mini-window') {
			return { rooms: [], index: -1 };
		}
		const rooms = this.miniRoomList;
		const index = rooms.indexOf(room.id);
		// index === -1: shouldn't happen
		return { rooms, index };
	}
	fullNav(room = this.room) {
		const rooms = ['' as RoomID, ...this.miniRoomList, ...this.leftRoomList.slice(1), ...this.rightRoomList];
		const index = rooms.indexOf(room.id);
		return { rooms, index };
	}
	focusLeftRoom() {
		const { rooms, index } = this.horizontalNav();
		if (index === -1) return;

		if (index === 0) {
			return this.focusRoom(rooms[rooms.length - 1]);
		}
		return this.focusRoom(rooms[index - 1]);
	}
	focusRightRoom() {
		const { rooms, index } = this.horizontalNav();
		if (index === -1) return;

		if (index === rooms.length - 1) {
			return this.focusRoom(rooms[0]);
		}
		return this.focusRoom(rooms[index + 1]);
	}
	focusUpRoom() {
		const { rooms, index } = this.verticalNav();
		if (index === -1) return;

		if (index === 0) {
			return this.focusRoom(rooms[rooms.length - 1]);
		}
		return this.focusRoom(rooms[index - 1]);
	}
	focusDownRoom() {
		const { rooms, index } = this.verticalNav();
		if (index === -1) return;

		if (index === rooms.length - 1) {
			return this.focusRoom(rooms[0]);
		}
		return this.focusRoom(rooms[index + 1]);
	}
	focusUnreadRoom(direction: 'left' | 'right') {
		const { rooms, index } = this.fullNav();
		if (index === -1) return;

		const unreadRooms = rooms.filter((room, i) =>
			(PS.rooms[room]?.isSubtleNotifying || PS.rooms[room]?.notifications.length) &&
			(direction === 'left' ? i < index : i > index)
		);

		if (!unreadRooms.length) return;

		const target = direction === 'left' ? unreadRooms[unreadRooms.length - 1] : unreadRooms[0];

		return this.focusRoom(target);
	}
	alert(message: string, opts: { okButton?: string, parentElem?: HTMLElement | null, width?: number } = {}) {
		// cherish-alert-mask:begin (patch-alert-mask.py)
		// 한글이 없는 알림은 유저에게 안 보인다 (2026-09-24). 서버 |popup| 과 클라 자체 알림이 전부
		// 여기를 지난다 — 자리마다 막으면 업스트림의 영어 팝업 수백 자리가 하나씩 샌다.
		// 원문은 콘솔에만 남긴다. 우리 문구는 한글화 패치가 빌드 전에 바꿔 두므로 걸리지 않는다.
		if (message && !/[\uac00-\ud7a3]/.test(message)) {
			console.warn('[cherish-alert-mask]', message);
			message = /because you were not in that room/.test(message) ?
				'연결이 잠깐 끊겼다 이어져서 방금 누른 게 전달되지 않았어요.\n다시 한 번 눌러 주세요.' :
				'요청을 처리하지 못했어요.\n잠시 뒤에 다시 해 주세요.';
			opts = { ...opts, width: undefined };
		}
		// cherish-alert-mask:end
		this.join(`popup-${this.popups.length}` as RoomID, {
			args: { message, ...opts, parentElem: null },
			parentElem: opts.parentElem,
		});
	}
	confirm(message: string, opts: {
		okButton?: string, cancelButton?: string,
		otherButtons?: preact.ComponentChildren, parentElem?: HTMLElement,
	} = {}) {
		// cherish-cancel-ko-confirm:begin (patch-alert-mask.py)
		// 기본 취소 버튼이 'Cancel' 이라 cancelButton 을 안 넘긴 확인 창엔 전부 영어 버튼이 붙었다.
		opts.cancelButton ??= '취소';
		// cherish-cancel-ko-confirm:end
		return new Promise<boolean>(resolve => {
			this.join(`popup-${this.popups.length}` as RoomID, {
				args: { message, okValue: true, cancelValue: false, callback: resolve, ...opts, parentElem: null },
				parentElem: opts.parentElem,
			});
		});
	}
	prompt(message: string, opts: {
		defaultValue?: string, okButton?: string, cancelButton?: string, type?: 'text' | 'password' | 'number' | 'numeric',
		label?: string, otherButtons?: preact.ComponentChildren, parentElem?: HTMLElement | null,
	} = {}): Promise<string | null> {
		// cherish-cancel-ko-prompt:begin (patch-alert-mask.py)
		// 기본 취소 버튼이 'Cancel' 이라 cancelButton 을 안 넘긴 확인 창엔 전부 영어 버튼이 붙었다.
		opts.cancelButton ??= '취소';
		// cherish-cancel-ko-prompt:end
		return new Promise(resolve => {
			this.join(`popup-${this.popups.length}` as RoomID, {
				args: {
					message, value: opts.defaultValue || '',
					okValue: true, cancelValue: null, callback: resolve, ...opts, parentElem: null,
				},
				parentElem: opts.parentElem,
			});
		});
	}
	getPMRoom(userid: ID): ChatRoom {
		const myUserid = PS.user.userid;
		const roomid = `dm-${[userid, myUserid].sort().join('-')}` as RoomID;
		if (this.rooms[roomid]) return this.rooms[roomid] as ChatRoom;
		this.join(roomid);
		return this.rooms[roomid]! as ChatRoom;
	}
	/**
	 * Low-level add room. You usually want `join`.
	 *
	 * By default, focuses the room after adding it. (`options.autofocus = false` to suppress)
	 *
	 * By default, when autofocusing, closes popups that aren't the parent of the added room.
	 * (`options.autoclosePopups = false` to suppress)
	 */
	addRoom(options: RoomOptions & { autoclosePopups?: boolean, autofocus?: boolean }) {
		options.autofocus ??= true;
		options.autoclosePopups ??= options.autofocus;
		// support hardcoded PM room-IDs
		if (options.id.startsWith('challenge-')) {
			this.requestNotifications();
			options.id = `dm-${options.id.slice(10)}` as RoomID;
			options.args = { challengeMenuOpen: true, ...options.args };
		}
		if (options.id.startsWith('dm-')) {
			this.requestNotifications();
			if (options.id.length >= 5 && options.id.endsWith('--')) {
				options.id = options.id.slice(0, -2) as RoomID;
				options.args = { initialSlash: true };
			}
		}
		if (options.id.startsWith('battle-') && PS.prefs.rightpanelbattles) options.location = 'right';
		if (options.id.startsWith('help-')) {
			options.location = 'right';
			options.type = 'chat';
		}
		options.parentRoomid ??= this.getRoom(options.parentElem)?.id;
		const parentRoom = options.parentRoomid ? this.rooms[options.parentRoomid] : null;
		let preexistingRoom = this.rooms[options.id];
		if (preexistingRoom && this.isPopup(preexistingRoom)) {
			const sameOpener = (preexistingRoom.parentElem === options.parentElem);
			this.closePopupsAbove(parentRoom, true);
			if (sameOpener) return;
			preexistingRoom = this.rooms[options.id];
		}
		if (preexistingRoom) {
			if (options.args?.format) {
				preexistingRoom.args = options.args;
				if ((preexistingRoom as ChatRoom).challengeMenuOpen) {
					options.args.format = `!!${options.args.format as string}`;
				}
			}
			if (options.autofocus) {
				if (options.args?.challengeMenuOpen) {
					(preexistingRoom as ChatRoom).openChallenge();
				}
				this.focusRoom(preexistingRoom.id);
			}
			return preexistingRoom;
		}
		if (options.autoclosePopups) {
			let parentPopup = parentRoom;
			if ((options.parentElem as HTMLButtonElement)?.name === 'closeRoom') {
				// We want to close all popups above the parent element.
				// This is usually the parent room, but if we're clicking Close
				// in the overflow tablist, the close button's parent room is
				// the tab rather than the overflow tablist popup,
				// which needs to be corrected here.
				parentPopup = PS.rooms['roomtablist'] || parentPopup;
			}
			this.closePopupsAbove(parentPopup, true);
		}
		const room = this.createRoom(options);
		this.rooms[room.id] = room;
		const location = room.location;
		room.location = null!;
		this.moveRoom(room, location, !options.autofocus);
		if (options.backlog) {
			for (const args of options.backlog) {
				room.receiveLine(args);
			}
		}
		if (options.autofocus) room.focusNextUpdate = true;
		return room;
	}
	hideRightRoom() {
		if (PS.rightPanel) {
			if (PS.panel === PS.rightPanel) PS.panel = PS.leftPanel;
			if (PS.room === PS.rightPanel) PS.room = PS.leftPanel;
			PS.rightPanel = null;
			PS.update();
			PS.focusRoom(PS.leftPanel.id);
		}
	}
	renameRoom(room: PSRoom, id: RoomID) {
		// should never happen
		if (this.rooms[id]) this.removeRoom(this.rooms[id]);

		const oldid = room.id;
		room.id = id;
		this.rooms[id] = room;
		delete this.rooms[oldid];

		const popupIndex = this.popups.indexOf(oldid);
		if (popupIndex >= 0) this.popups[popupIndex] = id;
		const leftRoomIndex = this.leftRoomList.indexOf(oldid);
		if (leftRoomIndex >= 0) this.leftRoomList[leftRoomIndex] = id;
		const rightRoomIndex = this.rightRoomList.indexOf(oldid);
		if (rightRoomIndex >= 0) this.rightRoomList[rightRoomIndex] = id;
		const miniRoomIndex = this.miniRoomList.indexOf(oldid);
		if (miniRoomIndex >= 0) this.miniRoomList[miniRoomIndex] = id;

		this.update();
	}
	isPopup(room: PSRoom | undefined | null) {
		if (!room) return false;
		return room.location === 'popup' || room.location === 'modal-popup';
	}
	/** this isn't just !isPopup. you forgot about mini windows again. */
	isPanel(room: PSRoom | undefined | null) {
		if (!room) return false;
		return room.location === 'left' || room.location === 'right';
	}
	moveRoom(room: PSRoom, location: PSRoomLocation, background?: boolean, index?: number) {
		location = this.normalizeRoomLocation(location);
		if (room.location === location && index === undefined) {
			if (background === true) {
				if (room === this.leftPanel) {
					this.leftPanel = this.mainmenu;
					this.panel = this.mainmenu;
				} else if (room === this.rightPanel) {
					this.rightPanel = this.rooms['rooms'] || null;
					this.panel = this.rightPanel || this.leftPanel;
				}
			} else if (background === false) {
				this.focusRoom(room.id);
			}
			return;
		}
		if (this.isPopup(room) && (location === 'popup' || location === 'modal-popup')) {
			room.location = location;
			return;
		}

		background ??= !this.isVisible(room);

		if (room.location === 'mini-window') {
			const miniRoomIndex = this.miniRoomList.indexOf(room.id);
			if (miniRoomIndex >= 0) {
				this.miniRoomList.splice(miniRoomIndex, 1);
			}
			if (this.room === room) this.room = this.panel;
		} else if (room.location === 'popup' || room.location === 'modal-popup') {
			const popupIndex = this.popups.indexOf(room.id);
			if (popupIndex >= 0) {
				this.popups.splice(popupIndex, 1);
			}
			if (this.room === room) this.room = this.panel;
		} else if (room.location === 'left') {
			const leftRoomIndex = this.leftRoomList.indexOf(room.id);
			if (leftRoomIndex >= 0) {
				this.leftRoomList.splice(leftRoomIndex, 1);
			}
			if (this.room === room) this.room = this.mainmenu;
			if (this.panel === room) this.panel = this.mainmenu;
			if (this.leftPanel === room) this.leftPanel = this.mainmenu;
		} else if (room.location === 'right') {
			const rightRoomIndex = this.rightRoomList.indexOf(room.id);
			if (rightRoomIndex >= 0) {
				this.rightRoomList.splice(rightRoomIndex, 1);
			}
			if (this.room === room) this.room = this.rooms['rooms'] || this.leftPanel;
			if (this.panel === room) this.panel = this.rooms['rooms'] || this.leftPanel;
			if (this.rightPanel === room) this.rightPanel = this.rooms['rooms'] || null;
		}

		room.location = location;
		switch (location) {
		case 'left':
			this.leftRoomList.splice(Math.max(index ?? Infinity, 1), 0, room.id);
			break;
		case 'right':
			this.rightRoomList.splice(Math.min(index ?? -1, this.rightRoomList.length - 1), 0, room.id);
			break;
		case 'mini-window':
			this.miniRoomList.splice(index ?? 0, 0, room.id);
			break;
		case 'popup':
		case 'modal-popup':
			// moving a room to a popup must move it to the topmost popup
			this.popups.push(room.id);
			this.room = room; // popups can't be backgrounded
			break;
		default:
			throw new Error(`Invalid room location: ${location satisfies never as string}`);
		}
		if (!background) {
			if (location === 'left') this.leftPanel = this.panel = room;
			if (location === 'right') this.rightPanel = this.panel = room;
			if (location === 'mini-window') this.leftPanel = this.panel = this.mainmenu;
			this.room = room;
			room.focusNextUpdate = true;
		}
	}
	removeRoom(room: PSRoom) {
		const wasFocused = this.room === room;
		delete PS.rooms[room.id];

		const leftRoomIndex = PS.leftRoomList.indexOf(room.id);
		if (leftRoomIndex >= 0) {
			PS.leftRoomList.splice(leftRoomIndex, 1);
		}
		if (PS.leftPanel === room) {
			PS.leftPanel = this.mainmenu;
			if (PS.panel === room) PS.panel = this.mainmenu;
			if (PS.room === room) PS.room = this.mainmenu;
		}

		const rightRoomIndex = PS.rightRoomList.indexOf(room.id);
		if (rightRoomIndex >= 0) {
			PS.rightRoomList.splice(rightRoomIndex, 1);
		}
		if (PS.rightPanel === room) {
			let newRightRoomid = PS.rightRoomList[rightRoomIndex] || PS.rightRoomList[rightRoomIndex - 1];
			PS.rightPanel = newRightRoomid ? PS.rooms[newRightRoomid]! : null;
			if (PS.panel === room) PS.panel = PS.rightPanel || PS.leftPanel;
			if (PS.room === room) PS.room = PS.panel;
		}

		if (room.location === 'mini-window') {
			const miniRoomIndex = PS.miniRoomList.indexOf(room.id);
			if (miniRoomIndex >= 0) {
				PS.miniRoomList.splice(miniRoomIndex, 1);
			}
			if (PS.room === room) {
				PS.room = PS.rooms[PS.miniRoomList[miniRoomIndex]] || PS.rooms[PS.miniRoomList[miniRoomIndex - 1]] || PS.mainmenu;
			}
		}

		if (this.popups.length && room.id === this.popups[this.popups.length - 1]) {
			this.popups.pop();
			if (wasFocused) {
				// the topmost popup must be focused while open, but if you focus
				// while closing it, don't steal focus back.
				if (this.popups.length) {
					// focus topmost popup
					PS.room = PS.rooms[this.popups[this.popups.length - 1]]!;
				} else {
					// if popup parent is a mini-window, focus popup parent
					PS.room = PS.rooms[room.parentRoomid ?? PS.panel.id] || PS.panel;
					// otherwise focus current panel
					if (PS.room.location !== 'mini-window' || PS.panel !== PS.mainmenu) PS.room = PS.panel;
				}
			}
		}

		if (wasFocused) {
			this.room.focusNextUpdate = { preventScroll: true };
		}
		room.destroy();
	}
	/** do NOT use this in a while loop: see `closePopupsUntil */
	closePopup(skipUpdate?: boolean) {
		if (!this.popups.length) return;
		this.leave(this.popups[this.popups.length - 1]);
		if (!skipUpdate) this.update();
	}
	closeAllPopups(skipUpdate?: boolean) {
		this.closePopupsAbove(null, skipUpdate);
	}
	closePopupsAbove(room: PSRoom | null | undefined, skipUpdate?: boolean) {
		if (!this.popups.length) return;
		// a while-loop may be simpler, but the loop invariant is very hard to prove
		// and any bugs (opening a popup while leaving a room) could lead to an infinite loop
		// a for-loop doesn't have that problem
		for (let i = this.popups.length - 1; i >= 0; i--) {
			if (this.popups[i] === room?.id) break;
			this.removeRoom(PS.rooms[this.popups[i]]!);
		}
		if (!skipUpdate) this.update();
	}
	// cherish-one-battle-tab:begin (patch-one-battle-tab.py)
	/** 방금 `leave` 로 닫은 배틀 방 → 닫은 시각. 되살아난 방을 가려낸다 (아래 cherishOneBattleTab). */
	cherishLeftBattles: { [roomid: string]: number } = {};
	/**
	 * 5초 안에 `leave` 로 닫은 배틀 방인가 — 서버의 늦은 `|init|` 으로 되살아나는 방을 가려낸다.
	 * `case 'init'` 의 autofocus 가 이걸 본다: 되살아난 방이 포커스를 뺏으면, 도로 닫을 때 「포커스 방이
	 * 닫힘」으로 홈에 떨어진다 (2026-10-10 「대진표로 돌아가기 → 메인화면」). 포커스를 안 주면 닫아도 조용하다.
	 */
	cherishRecentlyLeftBattle(roomid: string): boolean {
		const at = this.cherishLeftBattles[roomid];
		return !!at && Date.now() - at <= 5000;
	}
	/**
	 * 배틀 탭은 하나만 (2026-10-06, patch-one-battle-tab.py). 서버가 배틀 방을 채워 줄 때(`|init|`) 부른다.
	 *
	 * 그 방 말고 다른 배틀 탭은 내 판·관전·끝난 판 구분 없이 닫는다. 진행 중인 내 판은 기권시킨다
	 * (확인창 없음 — 사용자 결정). 서버가 내 배틀을 둘 못 열게 막으므로, 이게 도는 건 사실상
	 * 「다음 판이 열렸다」「관전하러 갔다」 둘뿐이다.
	 *
	 * ⚠️ 막 닫은 방은 서버의 늦은 `|init|` 으로 되살아난다(2026-09-28 토너먼트). 그 방이 규칙을 타면
	 *    지금 보던 새 판이 닫히고 기권된다 — 5초 안에 되살아난 방은 규칙 대신 그 방을 다시 닫는다.
	 * 닫기는 다음 틱에 한다. 지금은 이 방의 init 묶음을 처리하는 도중이다.
	 */
	cherishOneBattleTab(room: PSRoom) {
		const now = Date.now();
		for (const id in this.cherishLeftBattles) {
			if (now - this.cherishLeftBattles[id] > 5000) delete this.cherishLeftBattles[id];
		}
		if (this.cherishLeftBattles[room.id]) {
			// 포커스는 init 의 autofocus 예외가 이미 안 줬다 — 그래서 여기서 닫아도 화면이 홈으로 안 떨어진다.
			setTimeout(() => { if (PS.rooms[room.id] === room) PS.leave(room.id); }, 0);
			return;
		}
		const others: PSRoom[] = [];
		for (const id in PS.rooms) {
			const r = PS.rooms[id];
			if (!r || r === room || r.type !== 'battle') continue;
			if (!id.startsWith('battle-') || id.startsWith('battle-uploaded-')) continue;
			if ((r as any).args?.cherishLesson) continue;
			others.push(r);
		}
		if (!others.length) return;
		setTimeout(() => {
			for (const r of others) {
				if (PS.rooms[r.id] !== r) continue;
				const br = r as any;
				// 진행 중인 내 판. 그냥 닫으면 서버는 기권이 아니라 「자리 비움」 타이머를 돌린다.
				if (br.isPlaying?.() || br.requireForfeit) br.send('/forfeit');
				PS.leave(r.id);
			}
		}, 0);
	}
	// cherish-one-battle-tab:end
	/** Focus a room, creating it if it doesn't already exist. */
	join(roomid: RoomID, options?: Partial<RoomOptions> | null) {
		// cherish-one-battle-tab-join:begin (patch-one-battle-tab.py)
		delete this.cherishLeftBattles[roomid];
		// cherish-one-battle-tab-join:end
		// popups are always reopened rather than focused
		if (PS.rooms[roomid] && !PS.isPopup(PS.rooms[roomid])) {
			if (this.room.id === roomid) return;
			this.focusRoom(roomid);
			return;
		}
		this.addRoom({ id: roomid, ...options });
		this.update();
	}
	leave(roomid: RoomID) {
		// cherish-world fork: 홈처럼 닫을 수 없는 기본 탭 (patch-cherish-tabs.py)
		if (roomid === 'cherish-nest' || roomid === 'cherish-gym' || roomid === 'cherish-ranking' || roomid === 'cherish-team') return;
		// cherish-world fork: 홈처럼 닫을 수 없는 기본 탭 (patch-cherish-tabs.py)
		if (roomid === 'cherish-achievements' || roomid === 'cherish-ranking' || roomid === 'cherish-team') return;
		if (!roomid || roomid === 'rooms') return;
		const room = PS.rooms[roomid];
		// cherish-one-battle-tab-leave:begin (patch-one-battle-tab.py)
		if (room?.type === 'battle') this.cherishLeftBattles[roomid] = Date.now();
		// cherish-one-battle-tab-leave:end
		if (room) {
			this.removeRoom(room);
			if (room.type === 'chat') this.updateAutojoin();
			this.update();
		}
	}

	updateAutojoin() {
		if (!PS.server.registered) return;
		let autojoins: string[] = [];
		let autojoinCount = 0;
		const rooms = [...this.miniRoomList, ...this.leftRoomList, ...this.rightRoomList];
		for (const roomid of rooms) {
			let room = PS.rooms[roomid] as ChatRoom;
			if (!room) return;
			if (room.type !== 'chat' || room.pmTarget) continue;
			autojoins.push(room.id.includes('-') ? room.id : (room.title || room.id));
			if (room.id === 'staff' || room.id === 'upperstaff' || (PS.server.id !== 'showdown' && room.id === 'lobby')) continue;
			autojoinCount++;
			if (autojoinCount >= 15) break;
		}

		const thisAutojoin = autojoins.join(',') || null;
		let autojoin = this.prefs.autojoin || null;
		if (this.server.id === 'showdown' && typeof autojoin !== 'object') {
			// Main server only mode
			if (autojoin === thisAutojoin) return;

			this.prefs.set('autojoin', thisAutojoin || null);
		} else {
			// Multi server mode
			autojoin = typeof autojoin === 'string' ? { showdown: autojoin } : autojoin || {};
			if (autojoin[this.server.id] === thisAutojoin) return;

			autojoin[this.server.id] = thisAutojoin || '';
			this.prefs.set('autojoin', autojoin);
		}
	}
	requestNotifications() {
		try {
			if (window.webkitNotifications?.requestPermission) {
				// Notification.requestPermission crashes Chrome 23:
				//   https://code.google.com/p/chromium/issues/detail?id=139594
				// In lieu of a way to detect Chrome 23, we'll just use the old
				// requestPermission API, which works to request permissions for
				// the new Notification spec anyway.
				window.webkitNotifications.requestPermission();
			} else if (window.Notification) {
				Notification.requestPermission?.(permission => {});
			}
		} catch {}
	}
	// cherish-match-sound:begin (patch-match-sound.py)
	/**
	 * 자동매칭 성공음 (2026-09-14). 서버 `|cherishmatched|` push 가 부른다.
	 *
	 * ⚠️ `Room.notify()` 와 달리 **방 포커스를 보지 않는다.** 업스트림은 창이 떠 있고 그 방이
	 *    보이면 소리를 건너뛰는데, 매칭을 기다리는 사람은 바로 그 화면을 보고 있다 —
	 *    그 규칙을 쓰면 정작 필요한 순간에 안 울린다.
	 * 음소거·알림 볼륨은 알림음과 같은 슬라이더를 그대로 따른다.
	 * ⚠️ 효과음 파일을 갈면 **이름을 바꿀 것** — audio/ 에는 캐시 버스터가 없어 CDN 이 4시간
	 *    동안 옛 파일을 내보낸다.
	 */
	playCherishMatchSound() {
		if (window.BattleSound && !this.prefs.mute) {
			window.BattleSound.playSound('audio/cherish/matchmaking-found.mp3', this.prefs.notifvolume);
		}
	}
	// cherish-match-sound:end
	playNotificationSound() {
		if (window.BattleSound && !this.prefs.mute) {
			window.BattleSound.playSound('audio/cherish/notification.mp3', this.prefs.notifvolume);
		}
	}
};
